import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DatePicker, Popover } from 'antd';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import Chart from 'chart.js/auto';
import {
  addMskCalendarDays,
  createMskCalendarDate,
  formatMskDateRangeLabel,
  formatMskShortDate,
  getMskRangeSpanDays,
  getMskToday,
  normalizeMskCalendarDate,
} from '../../utils/moscow-calendar-date';
import { useBackdropClose } from '../../hooks/useBackdropClose';
import './dashboard-sales-panel.css';

const CHART_IDS = {
  turnover: 'turnover',
  clientPurchases: 'clientPurchases',
  proxies: 'proxies',
  newProxies: 'newProxies',
  clients: 'clients',
};

const CHART_GRANULARITY_OPTIONS = [
  { id: 'day', label: 'Дни' },
  { id: 'week', label: 'Недели' },
  { id: 'month', label: 'Месяцы' },
];

export const resolveAutoChartGranularity = (periodFilter, rangeStart, rangeEnd) => {
  const spanDays = getMskRangeSpanDays(rangeStart, rangeEnd);
  if (periodFilter === 'day' || spanDays <= 1) {
    return 'hour';
  }
  if (spanDays <= 31) {
    return 'day';
  }
  return 'month';
};

const parseChartApiDate = (value) => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null;
  }
  const [year, month, day] = value.split('-').map(Number);
  return createMskCalendarDate(year, month, day);
};

const GEO_FILTERS = [
  { id: 'all', label: 'Все' },
  { id: 'msk', label: 'Москва' },
  { id: 'spb', label: 'Санкт-Петербург' },
];

const PERIOD_FILTERS = [
  { id: 'day', label: 'День' },
  { id: 'week', label: 'Неделя' },
  { id: 'month', label: 'Месяц' },
  { id: 'year', label: 'Год' },
];

const MOBILE_PERIOD_FILTERS = PERIOD_FILTERS;
const DASHBOARD_DATE_POPOVER_MOBILE_MAX_WIDTH = 768;

export const SALES_DATA_FILTERS = [
  { id: 'turnover', label: 'Оборот' },
  { id: 'proxies', label: 'Прокси' },
  { id: 'new_proxies', label: 'Новые прокси' },
  { id: 'users', label: 'Пользователи' },
];

export const SALES_DATA_SECTION_IDS = SALES_DATA_FILTERS.map((item) => item.id);

export const SALES_DATA_SECTIONS_DEFAULT = [...SALES_DATA_SECTION_IDS];

export const normalizeVisibleDataSections = (sections) => {
  if (!Array.isArray(sections)) {
    return [...SALES_DATA_SECTIONS_DEFAULT];
  }
  return SALES_DATA_SECTION_IDS.filter((sectionId) => sections.includes(sectionId));
};

export const isSalesDataSectionHidden = (visibleDataSections, sectionId) => {
  if (!Array.isArray(visibleDataSections)) {
    return false;
  }
  return !visibleDataSections.includes(sectionId);
};

export const SALES_DATA_FILTER_CLASS_NAMES = {
  turnover: 'dashboard-page--sales-data-turnover',
  proxies: 'dashboard-page--sales-data-proxies',
  new_proxies: 'dashboard-page--sales-data-new-proxies',
  users: 'dashboard-page--sales-data-users',
};

const SALES_FILTER_DEFAULTS = {
  visibleDataSections: SALES_DATA_SECTIONS_DEFAULT,
  geoFilter: 'all',
  periodFilter: 'week',
};

const PERIOD_CONFIG = {
  day: { days: 1, step: 1 },
  week: { days: 7, step: 7 },
  month: { days: 30, step: 30 },
  year: { days: 365, step: 365 },
};

const MONTH_SHORT = ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'];

const CLIENT_PURCHASE_CHART_SERIES = [
  { id: 'new', label: 'Новые', color: '#0CBE90' },
  { id: 'renewal', label: 'Продление', color: '#5283F4' },
  { id: 'total', label: 'Всего', color: '#F33F4E' },
];

const CHART_SERIES = [
  { id: 'individual', label: 'Индивидуальный', color: '#5283F4' },
  { id: 'shared', label: 'Общий', color: '#0CBE90' },
  { id: 'sharedPlus', label: 'Общий+', color: '#8E94A1' },
  { id: 'total', label: 'Всего', color: '#F33F4E' },
];

const CHART_Y_MAX = 40000;
const CLIENTS_CHART_Y_MAX = 40;

const CLIENTS_CHART_SERIES = [
  { id: 'active', label: 'Активные', color: '#5283F4' },
  { id: 'new', label: 'Новые', color: '#0CBE90' },
  { id: 'registrations', label: 'Регистрации', color: '#8E94A1' },
  { id: 'churned', label: 'Ушедшие', color: '#F33F4E' },
];

const formatSeriesLegendLabel = (item) => item?.label || '';

const formatSeriesTooltipLabel = (item) => item?.label || '';
const CHART_SVG_WIDTH = 600;
const CHART_SVG_HEIGHT = 160;
const WEEKDAY_SHORT = ['ВС', 'ПН', 'ВТ', 'СР', 'ЧТ', 'ПТ', 'СБ'];

/** Значения линий «Оборот» — макет Figma (node 884:45857). */
const FIGMA_CHART_VALUES = {
  individual: [14000, 16000, 13000, 1000, 7000, 5000, 3000],
  shared: [4000, 7000, 7000, 2000, 30000, 12000, 10000],
  sharedPlus: [7000, 8000, 1000, 13000, 8000, 11000, 9000],
  total: [24000, 32000, 22000, 14000, 48000, 28000, 40000],
};

/** Значения линий «Прокси» — макет Figma. */
const FIGMA_PROXY_CHART_VALUES = {
  individual: [180, 170, 110, 150, 200, 160, 170],
  shared: [130, 140, 120, 125, 145, 135, 130],
  sharedPlus: [210, 180, 80, 120, 160, 200, 170],
  total: [480, 400, 200, 250, 350, 400, 320],
};

/** Значения линий «Клиенты» — макет Figma. */
const FIGMA_CLIENTS_CHART_VALUES = {
  active: [45, 44, 43, 42, 43, 44, 45],
  new: [8, 7, 6, 5, 7, 6, 8],
  registrations: [10, 9, 8, 7, 9, 8, 10],
  churned: [5, 4, 3, 2, 4, 3, 5],
};

const PROXY_CHART_Y_MAX = 400;

const formatMoney = (value, withCurrency = false) => {
  const numericValue = Number(value || 0);
  const formatted = numericValue.toLocaleString('ru-RU');
  return withCurrency ? `${formatted} ₽` : formatted;
};

const formatPercent = (value) => `${Math.round(Number(value || 0))}%`;

const addDays = addMskCalendarDays;
const formatShortDate = formatMskShortDate;
const formatDateRangeLabel = formatMskDateRangeLabel;
const getToday = getMskToday;
const getRangeSpanDays = getMskRangeSpanDays;
const startOfDay = normalizeMskCalendarDate;

const inferPeriodFromSpan = (spanDays) => {
  if (spanDays <= 1) {
    return 'day';
  }
  if (spanDays <= 7) {
    return 'week';
  }
  if (spanDays <= 30) {
    return 'month';
  }
  return 'year';
};

const buildRollingRange = (periodFilter, endDate = getToday()) => {
  const rangeEnd = startOfDay(endDate);
  const { days } = getPeriodConfig(periodFilter);
  const rangeStart = addDays(rangeEnd, -(days - 1));
  return { rangeStart, rangeEnd };
};

const getPeriodConfig = (periodFilter) => PERIOD_CONFIG[periodFilter] || PERIOD_CONFIG.week;

const formatDateRangeLabelForRange = (rangeStart, rangeEnd) => {
  if (getRangeSpanDays(rangeStart, rangeEnd) === 1) {
    return formatShortDate(rangeStart);
  }
  return formatDateRangeLabel(rangeStart, rangeEnd);
};

const DAY_CHART_BUCKETS = 24;

const isHourlyChartRange = (periodFilter, rangeStart, rangeEnd, chartGranularity = null) => {
  if (chartGranularity === 'hour') {
    return true;
  }
  if (chartGranularity && chartGranularity !== 'hour') {
    return false;
  }
  if (periodFilter === 'day') {
    return true;
  }
  return getRangeSpanDays(rangeStart, rangeEnd) <= 1;
};

const getChartGranularity = (salesData, periodFilter, rangeStart, rangeEnd, preferredGranularity = null) => {
  if (preferredGranularity) {
    return preferredGranularity;
  }
  const fromMeta = String(salesData?.meta?.chart_granularity || '').trim().toLowerCase();
  if (fromMeta) {
    return fromMeta;
  }
  return resolveAutoChartGranularity(periodFilter, rangeStart, rangeEnd);
};

const getChartBucketCount = (periodFilter, salesData, rangeStart, rangeEnd, chartGranularity = null) => {
  const granularity = getChartGranularity(
    salesData,
    periodFilter,
    rangeStart,
    rangeEnd,
    chartGranularity,
  );
  if (isHourlyChartRange(periodFilter, rangeStart, rangeEnd, granularity)) {
    return DAY_CHART_BUCKETS;
  }
  const fromMeta = Number(salesData?.meta?.chart_buckets);
  if (fromMeta > 0) {
    return fromMeta;
  }
  const spanDays = getRangeSpanDays(rangeStart, rangeEnd);
  if (granularity === 'month') {
    return Math.max(
      1,
      (rangeEnd.getFullYear() - rangeStart.getFullYear()) * 12
        + (rangeEnd.getMonth() - rangeStart.getMonth())
        + 1,
    );
  }
  if (granularity === 'week') {
    return Math.max(1, Math.ceil(spanDays / 7));
  }
  return spanDays;
};

const getChartBucketRanges = (salesData) => (
  Array.isArray(salesData?.meta?.chart_bucket_ranges)
    ? salesData.meta.chart_bucket_ranges
    : []
);

const formatChartBucketRangeLabel = (bucketRange, granularity) => {
  if (!bucketRange || typeof bucketRange !== 'object') {
    return '';
  }
  if (granularity === 'hour') {
    const from = String(bucketRange.from || '');
    const to = String(bucketRange.to || '');
    if (from && to) {
      return `${from}–${to}`;
    }
    return from || to;
  }
  const startDate = parseChartApiDate(bucketRange.from);
  const endDate = parseChartApiDate(bucketRange.to);
  if (!startDate || !endDate) {
    return '';
  }
  if (startDate.getTime() === endDate.getTime()) {
    return formatShortDate(startDate);
  }
  return `${formatShortDate(startDate)} – ${formatShortDate(endDate)}`;
};

const buildXAxisLabels = (rangeStart, rangeEnd, bucketCount, chartGranularity = null, bucketRanges = []) => {
  const spanDays = getRangeSpanDays(rangeStart, rangeEnd);
  const count = bucketCount || spanDays;
  const granularity = chartGranularity
    || (spanDays <= 1 ? 'hour' : (spanDays <= 30 ? 'day' : 'month'));

  if (Array.isArray(bucketRanges) && bucketRanges.length === count) {
    if (granularity === 'hour') {
      return bucketRanges.map((range, index) => (
        index % 3 === 0 ? String(range?.from || '').slice(0, 2) : ''
      ));
    }
    if (granularity === 'month') {
      return bucketRanges.map((range) => {
        const date = parseChartApiDate(range?.from);
        return date ? MONTH_SHORT[date.getMonth()] : '';
      });
    }
    if (granularity === 'week') {
      return bucketRanges.map((range) => {
        const startDate = parseChartApiDate(range?.from);
        return startDate ? formatShortDate(startDate) : '';
      });
    }
    return bucketRanges.map((range, index) => {
      const date = parseChartApiDate(range?.from);
      if (!date) {
        return '';
      }
      if (count > 31 && index % Math.ceil(count / 16) !== 0) {
        return '';
      }
      const day = String(date.getDate()).padStart(2, '0');
      if (spanDays > 7) {
        const month = String(date.getMonth() + 1).padStart(2, '0');
        return `${day}.${month}`;
      }
      const weekday = WEEKDAY_SHORT[date.getDay()];
      return `${day}, ${weekday}`;
    });
  }

  if (!(rangeStart instanceof Date) || Number.isNaN(rangeStart.getTime())) {
    return ['02, ПН', '03, ВТ', '04, СР', '05, ЧТ', '06, ПТ', '07, СБ', '08, ВС'].slice(0, count);
  }

  if (granularity === 'month') {
    return Array.from({ length: count }, (_, index) => {
      const date = new Date(rangeStart.getFullYear(), rangeStart.getMonth() + index, 1);
      return MONTH_SHORT[date.getMonth()];
    });
  }

  if (granularity === 'week') {
    return Array.from({ length: count }, (_, index) => {
      const date = addDays(rangeStart, index * 7);
      return date ? formatShortDate(date) : '';
    });
  }

  if (granularity === 'hour' || spanDays === 1 || count === DAY_CHART_BUCKETS) {
    return Array.from({ length: count }, (_, index) => {
      if (index % 3 !== 0) {
        return '';
      }
      return String(index).padStart(2, '0');
    });
  }

  return Array.from({ length: count }, (_, index) => {
    const date = addDays(rangeStart, index);
    const day = String(date.getDate()).padStart(2, '0');
    if (spanDays > 7) {
      const month = String(date.getMonth() + 1).padStart(2, '0');
      return `${day}.${month}`;
    }
    const weekday = WEEKDAY_SHORT[date.getDay()];
    return `${day}, ${weekday}`;
  });
};

const buildChartPolyline = (values, yMax, width = CHART_SVG_WIDTH, height = CHART_SVG_HEIGHT) => {
  const normalizedValues = Array.isArray(values) && values.length > 0 ? values : [0];
  const step = normalizedValues.length > 1 ? width / (normalizedValues.length - 1) : 0;

  return normalizedValues
    .map((value, index) => {
      const x = step * index;
      const clampedValue = Math.max(0, Math.min(yMax, Number(value) || 0));
      const y = height - (clampedValue / yMax) * height;
      return `${x},${y}`;
    })
    .join(' ');
};

const resolveLineChartPointCount = (xAxisLabels, seriesValues, series) => {
  if (Array.isArray(xAxisLabels) && xAxisLabels.length > 0) {
    return xAxisLabels.length;
  }
  const firstSeriesId = series?.[0]?.id;
  const firstSeriesValues = firstSeriesId ? seriesValues?.[firstSeriesId] : null;
  return Array.isArray(firstSeriesValues) && firstSeriesValues.length > 0
    ? firstSeriesValues.length
    : 1;
};

const resolveChartBucketIndex = (offsetX, plotWidth, bucketCount) => {
  if (bucketCount <= 1) {
    return 0;
  }
  const ratio = Math.max(0, Math.min(1, offsetX / plotWidth));
  return Math.round(ratio * (bucketCount - 1));
};

const resolveChartBucketXPercent = (bucketIndex, bucketCount) => {
  if (bucketCount <= 1) {
    return 50;
  }
  return (bucketIndex / (bucketCount - 1)) * 100;
};

const resolveChartBucketLabelStyle = (bucketIndex, bucketCount) => {
  const leftPercent = resolveChartBucketXPercent(bucketIndex, bucketCount);
  if (bucketCount <= 1) {
    return { left: '50%', transform: 'translateX(-50%)' };
  }
  if (bucketIndex === 0) {
    return { left: `${leftPercent}%`, transform: 'translateX(0)' };
  }
  if (bucketIndex === bucketCount - 1) {
    return { left: `${leftPercent}%`, transform: 'translateX(-100%)' };
  }
  return { left: `${leftPercent}%`, transform: 'translateX(-50%)' };
};

const getChartBucketLabel = (xAxisLabels, bucketIndex, bucketRanges = [], chartGranularity = null) => {
  const rangeLabel = formatChartBucketRangeLabel(bucketRanges?.[bucketIndex], chartGranularity);
  if (rangeLabel) {
    return rangeLabel;
  }
  const directLabel = xAxisLabels?.[bucketIndex];
  if (directLabel) {
    return directLabel;
  }
  if (Array.isArray(xAxisLabels) && xAxisLabels.length === 24) {
    return `${String(bucketIndex).padStart(2, '0')}:00`;
  }
  return String(bucketIndex + 1);
};

const getChartPointCoords = (bucketIndex, value, yMax, bucketCount) => {
  const numericValue = Math.max(0, Math.min(yMax, Number(value) || 0));
  const x = bucketCount <= 1
    ? CHART_SVG_WIDTH / 2
    : (bucketIndex / (bucketCount - 1)) * CHART_SVG_WIDTH;
  const y = CHART_SVG_HEIGHT - (numericValue / yMax) * CHART_SVG_HEIGHT;
  return { x, y };
};

const defaultChartValueFormatter = (value) => Number(value || 0).toLocaleString('ru-RU');

/** Строки тарифов — как в макете Figma. */
const FIGMA_TARIFF_ROWS = [
  { tariff: 'Индивидуальный', avgCheck: 534, msk: 5000, spb: 15000, total: 25000 },
  { tariff: 'Общий+', avgCheck: 674, msk: 31000, spb: 60000, total: 91000 },
  { tariff: 'Общий', avgCheck: 456, msk: 121000, spb: 121000, total: 121000 },
];

const FIGMA_GEO_SUMMARY = {
  total: 132499,
  totalPercent: 100,
  spbAmount: 100000,
  spbPercent: 75,
  mskAmount: 32499,
  mskPercent: 25,
};

/** Сводка «Новые / Продление» — доля от оборота (макет). */
const FIGMA_CLIENT_PURCHASE_TYPE_SUMMARY = {
  total: 132499,
  totalPercent: 100,
  newAmount: 45000,
  newPercent: 34,
  renewalAmount: 87499,
  renewalPercent: 66,
};

/** Тарифы «Новые / Продление» — макет Figma. */
const FIGMA_CLIENT_PURCHASE_TARIFF_ROWS = [
  { tariff: 'Индивидуальный', avgCheck: 534, new: 8000, renewal: 17000, total: 25000 },
  { tariff: 'Общий+', avgCheck: 674, new: 28000, renewal: 63000, total: 91000 },
  { tariff: 'Общий', avgCheck: 456, new: 9000, renewal: 112000, total: 121000 },
];

/** Значения линий «Продажи» — Всего / Новые / Продление (макет). */
const FIGMA_CLIENT_PURCHASE_CHART_VALUES = {
  total: [9000, 11500, 7500, 7300, 18500, 12000, 9500],
  new: [3000, 4000, 2500, 2500, 6300, 4000, 3200],
  renewal: [6000, 7500, 5000, 4800, 12200, 8000, 6300],
};

const FIGMA_PROXY_GEO_SUMMARY = {
  total: 2148,
  totalPercent: 100,
  spbAmount: 1600,
  spbPercent: 74,
  mskAmount: 548,
  mskPercent: 26,
};

const FIGMA_PROXY_TARIFF_ROWS = [
  { tariff: 'Индивидуальный', msk: 100, spb: 200, total: 300 },
  { tariff: 'Общий+', msk: 450, spb: 800, total: 1250 },
  { tariff: 'Общий', msk: 1600, spb: 3200, total: 4800 },
];

const FIGMA_CLIENTS_SUMMARY = {
  registrations: 24,
  registrationsPercent: 100,
  clients: 11,
  clientsPercent: 45,
  conversionPercent: 45,
};

const FIGMA_CLIENTS_PERIOD_ROWS = [
  { period: 'Новые', registrations: 40, clients: 20, cr: '50%' },
  { period: 'Регистрации', registrations: 150, clients: 50, cr: '' },
  { period: 'Ушедшие', registrations: 500, clients: 280, cr: '56%' },
];

/** Сводная таблица по периодам — макет Figma. */
const FIGMA_PERIOD_SUMMARY_ROWS = [
  {
    period: 'За 7 дней',
    turnover: 25000,
    turnoverMsk: 10000,
    turnoverSpb: 15000,
    avgCheck: 534,
    proxies: 300,
    proxiesMsk: 100,
    proxiesSpb: 200,
    registrations: 40,
    clients: 20,
    cr: '50%',
  },
  {
    period: 'За 30 дней',
    turnover: 91000,
    turnoverMsk: 31000,
    turnoverSpb: 60000,
    avgCheck: 674,
    proxies: 1250,
    proxiesMsk: 450,
    proxiesSpb: 800,
    registrations: 150,
    clients: 50,
    cr: '33,3%',
  },
  {
    period: 'За 90 дней',
    turnover: 121000,
    turnoverMsk: 121000,
    turnoverSpb: 121000,
    avgCheck: 456,
    proxies: 4800,
    proxiesMsk: 1600,
    proxiesSpb: 3200,
    registrations: 500,
    clients: 280,
    cr: '56%',
  },
];

const buildTariffRows = (salesData, geoFilter) => {
  if (Array.isArray(salesData?.tariff_rows) && salesData.tariff_rows.length > 0) {
    return salesData.tariff_rows.map((row) => ({
      tariff: row.tariff,
      avgCheck: Number(row.avg_check || 0),
      msk: Number(row.msk || 0),
      spb: Number(row.spb || 0),
      total: Number(row.total || 0),
    }));
  }

  const totalRevenue = Number(salesData?.revenue?.total || salesData?.revenue?.week || 132499);
  const scale = totalRevenue / FIGMA_GEO_SUMMARY.total;

  const rows = FIGMA_TARIFF_ROWS.map((row) => ({
    tariff: row.tariff,
    avgCheck: row.avgCheck,
    msk: Math.round(row.msk * scale),
    spb: Math.round(row.spb * scale),
    total: Math.round(row.total * scale),
  }));

  if (geoFilter === 'msk') {
    return rows.map((row) => ({
      ...row,
      spb: 0,
      total: row.msk,
    }));
  }

  if (geoFilter === 'spb') {
    return rows.map((row) => ({
      ...row,
      msk: 0,
      total: row.spb,
    }));
  }

  return rows;
};

const EMPTY_CLIENT_PURCHASE_TYPE_SUMMARY = {
  total: 0,
  totalPercent: 100,
  newAmount: 0,
  newPercent: 0,
  renewalAmount: 0,
  renewalPercent: 0,
};

const EMPTY_CLIENT_PURCHASE_TARIFF_ROWS = [
  { tariff: 'Индивидуальный', avgCheck: 0, new: 0, renewal: 0, total: 0 },
  { tariff: 'Общий+', avgCheck: 0, new: 0, renewal: 0, total: 0 },
  { tariff: 'Общий', avgCheck: 0, new: 0, renewal: 0, total: 0 },
];

const buildClientPurchaseTypeSummary = (salesData) => {
  const summary = salesData?.client_purchases?.type_summary;
  if (summary && typeof summary === 'object') {
    return {
      total: Number(summary.total || 0),
      totalPercent: Number(summary.total_percent ?? 100),
      newAmount: Number(summary.new_amount || 0),
      newPercent: Number(summary.new_percent || 0),
      renewalAmount: Number(summary.renewal_amount || 0),
      renewalPercent: Number(summary.renewal_percent || 0),
    };
  }

  if (salesData != null) {
    return { ...EMPTY_CLIENT_PURCHASE_TYPE_SUMMARY };
  }

  const total = FIGMA_CLIENT_PURCHASE_TYPE_SUMMARY.total;
  return {
    total,
    totalPercent: 100,
    newAmount: FIGMA_CLIENT_PURCHASE_TYPE_SUMMARY.newAmount,
    newPercent: FIGMA_CLIENT_PURCHASE_TYPE_SUMMARY.newPercent,
    renewalAmount: FIGMA_CLIENT_PURCHASE_TYPE_SUMMARY.renewalAmount,
    renewalPercent: FIGMA_CLIENT_PURCHASE_TYPE_SUMMARY.renewalPercent,
  };
};

const buildClientPurchaseTariffRows = (salesData) => {
  if (
    Array.isArray(salesData?.client_purchases?.tariff_rows)
    && salesData.client_purchases.tariff_rows.length > 0
  ) {
    return salesData.client_purchases.tariff_rows.map((row) => ({
      tariff: row.tariff,
      avgCheck: Number(row.avg_check || 0),
      new: Number(row.new || 0),
      renewal: Number(row.renewal || 0),
      total: Number(row.total || 0),
    }));
  }

  if (salesData != null) {
    return EMPTY_CLIENT_PURCHASE_TARIFF_ROWS.map((row) => ({ ...row }));
  }

  return FIGMA_CLIENT_PURCHASE_TARIFF_ROWS.map((row) => ({ ...row }));
};

const buildClientPurchaseChartSeries = (
  salesData,
  rangeStart,
  rangeEnd,
  periodFilter,
  chartGranularity = null,
) => {
  const bucketCount = getChartBucketCount(
    periodFilter,
    salesData,
    rangeStart,
    rangeEnd,
    chartGranularity,
  );
  const chart = salesData?.client_purchases?.chart;
  if (chart && typeof chart === 'object') {
    return normalizeChartSeries(chart, bucketCount, CLIENT_PURCHASE_CHART_KEYS);
  }
  if (salesData != null) {
    return normalizeChartSeries(
      { total: [], new: [], renewal: [] },
      bucketCount,
      CLIENT_PURCHASE_CHART_KEYS,
    );
  }
  return normalizeChartSeries(FIGMA_CLIENT_PURCHASE_CHART_VALUES, bucketCount, CLIENT_PURCHASE_CHART_KEYS);
};

const buildGeoSummary = (salesData) => {
  const geo = salesData?.geo_summary;
  if (geo && typeof geo === 'object') {
    return {
      total: Number(geo.total || 0),
      totalPercent: Number(geo.total_percent ?? 100),
      spbAmount: Number(geo.spb_amount || 0),
      spbPercent: Number(geo.spb_percent || 0),
      mskAmount: Number(geo.msk_amount || 0),
      mskPercent: Number(geo.msk_percent || 0),
    };
  }

  const total = Number(salesData?.revenue?.week || salesData?.revenue?.month || FIGMA_GEO_SUMMARY.total);
  const scale = total / FIGMA_GEO_SUMMARY.total;
  const spbAmount = Math.round(FIGMA_GEO_SUMMARY.spbAmount * scale);
  const mskAmount = Math.max(0, total - spbAmount);
  const spbPercent = total > 0 ? Math.round((spbAmount / total) * 100) : 0;
  const mskPercent = total > 0 ? 100 - spbPercent : 0;

  return {
    total,
    totalPercent: 100,
    spbAmount,
    spbPercent,
    mskAmount,
    mskPercent,
  };
};

const buildProxyTariffRows = (salesData, geoFilter, sectionKey = 'proxies') => {
  const section = salesData?.[sectionKey];
  if (Array.isArray(section?.tariff_rows) && section.tariff_rows.length > 0) {
    const rows = section.tariff_rows.map((row) => ({
      tariff: row.tariff,
      msk: Number(row.msk || 0),
      spb: Number(row.spb || 0),
      total: Number(row.total || 0),
    }));

    if (geoFilter === 'msk') {
      return rows.map((row) => ({
        ...row,
        spb: 0,
        total: row.msk,
      }));
    }

    if (geoFilter === 'spb') {
      return rows.map((row) => ({
        ...row,
        msk: 0,
        total: row.spb,
      }));
    }

    return rows;
  }

  const totalProxies = Number(
    section?.total || salesData?.proxies?.total || FIGMA_PROXY_GEO_SUMMARY.total,
  );
  const scale = totalProxies / FIGMA_PROXY_GEO_SUMMARY.total;

  const rows = FIGMA_PROXY_TARIFF_ROWS.map((row) => ({
    tariff: row.tariff,
    msk: Math.round(row.msk * scale),
    spb: Math.round(row.spb * scale),
    total: Math.round(row.total * scale),
  }));

  if (geoFilter === 'msk') {
    return rows.map((row) => ({
      ...row,
      spb: 0,
      total: row.msk,
    }));
  }

  if (geoFilter === 'spb') {
    return rows.map((row) => ({
      ...row,
      msk: 0,
      total: row.spb,
    }));
  }

  return rows;
};

const buildProxyGeoSummary = (salesData, geoFilter, sectionKey = 'proxies') => {
  const section = salesData?.[sectionKey];
  const geo = section?.geo_summary;
  if (geo && typeof geo === 'object') {
    const total = Number(geo.total || 0);
    const spbAmount = Number(geo.spb_amount || 0);
    const mskAmount = Number(geo.msk_amount || 0);
    const spbPercent = Number(geo.spb_percent || 0);
    const mskPercent = Number(geo.msk_percent || 0);

    if (geoFilter === 'msk') {
      return {
        total,
        totalPercent: 100,
        spbAmount: 0,
        spbPercent: 0,
        mskAmount: total,
        mskPercent: 100,
      };
    }

    if (geoFilter === 'spb') {
      return {
        total,
        totalPercent: 100,
        spbAmount: total,
        spbPercent: 100,
        mskAmount: 0,
        mskPercent: 0,
      };
    }

    return {
      total,
      totalPercent: Number(geo.total_percent ?? 100),
      spbAmount,
      spbPercent,
      mskAmount,
      mskPercent,
    };
  }

  const total = Number(
    section?.total || salesData?.proxies?.total || FIGMA_PROXY_GEO_SUMMARY.total,
  );
  const scale = total / FIGMA_PROXY_GEO_SUMMARY.total;
  const spbAmount = Math.round(FIGMA_PROXY_GEO_SUMMARY.spbAmount * scale);
  const mskAmount = Math.max(0, total - spbAmount);
  const spbPercent = total > 0 ? Math.round((spbAmount / total) * 100) : 0;
  const mskPercent = total > 0 ? 100 - spbPercent : 0;

  if (geoFilter === 'msk') {
    return {
      total,
      totalPercent: 100,
      spbAmount: 0,
      spbPercent: 0,
      mskAmount: total,
      mskPercent: 100,
    };
  }

  if (geoFilter === 'spb') {
    return {
      total,
      totalPercent: 100,
      spbAmount: total,
      spbPercent: 100,
      mskAmount: 0,
      mskPercent: 0,
    };
  }

  return {
    total,
    totalPercent: 100,
    spbAmount,
    spbPercent,
    mskAmount,
    mskPercent,
  };
};

const TURNOVER_CHART_KEYS = ['individual', 'shared', 'sharedPlus', 'total'];
const CLIENT_PURCHASE_CHART_KEYS = ['total', 'new', 'renewal'];
const CLIENTS_CHART_KEYS = ['active', 'new', 'registrations', 'churned'];

const normalizeChartSeries = (chart, bucketCount, seriesKeys = TURNOVER_CHART_KEYS) => {
  const padSeries = (values) => {
    const source = Array.isArray(values) ? values : [];
    if (bucketCount === DAY_CHART_BUCKETS && source.length === 1) {
      const expanded = Array(DAY_CHART_BUCKETS).fill(0);
      expanded[0] = Number(source[0] || 0);
      return expanded;
    }
    if (source.length === bucketCount) {
      return source;
    }
    if (source.length > bucketCount) {
      return source.slice(0, bucketCount);
    }
    return [...source, ...Array(bucketCount - source.length).fill(0)];
  };

  return seriesKeys.reduce((result, key) => {
    result[key] = padSeries(chart?.[key]);
    return result;
  }, {});
};

const buildSalesChartSeries = (
  salesData,
  rangeStart,
  rangeEnd,
  periodFilter,
  chartGranularity = null,
) => {
  const bucketCount = getChartBucketCount(
    periodFilter,
    salesData,
    rangeStart,
    rangeEnd,
    chartGranularity,
  );
  const chart = salesData?.chart;
  if (chart && typeof chart === 'object') {
    return normalizeChartSeries(chart, bucketCount);
  }
  if (salesData != null) {
    return normalizeChartSeries({}, bucketCount);
  }
  return normalizeChartSeries(FIGMA_CHART_VALUES, bucketCount);
};

const TURNOVER_Y_NICE_STEPS_K = [1, 2, 5, 10, 15, 20, 25, 50, 100, 200, 250, 500];
const TURNOVER_Y_NICE_STEPS_RUB = [
  100, 200, 250, 500, 1000, 2000, 2500, 5000, 10000, 15000, 20000, 25000, 50000, 100000,
];
const COUNT_CHART_NICE_STEPS = [1, 2, 5, 10, 20, 50, 100, 200, 400, 500];

const pickNiceCountStep = (rawStep) => {
  const step = Math.max(1, Math.ceil(rawStep));
  const niceStep = COUNT_CHART_NICE_STEPS.find((candidate) => candidate >= step);
  if (niceStep) {
    return niceStep;
  }
  return Math.ceil(step / 100) * 100;
};

const pickNiceThousandsStep = (stepK) => {
  const normalizedStep = Math.max(1, Math.ceil(stepK));
  const niceStep = TURNOVER_Y_NICE_STEPS_K.find((step) => step >= normalizedStep);
  if (niceStep) {
    return niceStep;
  }
  return Math.ceil(normalizedStep / 100) * 100;
};

const pickNiceMoneyStep = (rawStep) => {
  const step = Math.max(1, Math.ceil(rawStep));
  const niceRubStep = TURNOVER_Y_NICE_STEPS_RUB.find((candidate) => candidate >= step);
  if (niceRubStep) {
    return niceRubStep;
  }
  const stepK = step / 1000;
  return pickNiceThousandsStep(Math.ceil(stepK)) * 1000;
};

const computeTurnoverChartYMax = (seriesValues) => {
  const values = Object.values(seriesValues || {}).flat().map((value) => Number(value || 0));
  const maxVal = values.length > 0 ? Math.max(...values, 0) : 0;
  if (maxVal <= 0) {
    return CHART_Y_MAX;
  }
  const step = pickNiceMoneyStep(maxVal / 4);
  return step * 4;
};

const buildTurnoverYLabels = (yMax) => {
  const step = yMax / 4;
  return [4, 3, 2, 1].map((multiplier) => {
    const value = step * multiplier;
    if (value >= 1000) {
      return `${value / 1000}k`;
    }
    return String(Math.round(value));
  });
};

const buildProxyChartSeries = (
  salesData,
  rangeStart,
  rangeEnd,
  periodFilter,
  sectionKey = 'proxies',
  chartGranularity = null,
) => {
  const bucketCount = getChartBucketCount(
    periodFilter,
    salesData,
    rangeStart,
    rangeEnd,
    chartGranularity,
  );
  const chart = salesData?.[sectionKey]?.chart;
  if (chart && typeof chart === 'object') {
    return normalizeChartSeries(chart, bucketCount);
  }
  if (salesData != null) {
    return normalizeChartSeries({}, bucketCount);
  }
  return normalizeChartSeries(FIGMA_PROXY_CHART_VALUES, bucketCount);
};

const computeCountChartYMax = (seriesValues, fallbackMax) => {
  const values = Object.values(seriesValues || {}).flat().map((value) => Number(value || 0));
  const maxVal = values.length > 0 ? Math.max(...values, 0) : 0;
  if (maxVal <= 0) {
    return fallbackMax;
  }
  const step = pickNiceCountStep(maxVal / 4);
  return step * 4;
};

const buildCountYLabels = (yMax) => {
  const step = yMax / 4;
  return [4, 3, 2, 1].map((multiplier) => String(step * multiplier));
};

const computeProxyChartYMax = (seriesValues) => (
  computeCountChartYMax(seriesValues, PROXY_CHART_Y_MAX)
);

const buildProxyYLabels = (yMax) => buildCountYLabels(yMax);

const formatCount = (value) => Number(value || 0).toLocaleString('ru-RU');

const buildClientsChartSeries = (
  salesData,
  rangeStart,
  rangeEnd,
  periodFilter,
  chartGranularity = null,
) => {
  const bucketCount = getChartBucketCount(
    periodFilter,
    salesData,
    rangeStart,
    rangeEnd,
    chartGranularity,
  );
  const chart = salesData?.clients?.chart;
  if (chart && typeof chart === 'object') {
    return normalizeChartSeries(chart, bucketCount, CLIENTS_CHART_KEYS);
  }
  if (salesData != null) {
    return normalizeChartSeries({}, bucketCount, CLIENTS_CHART_KEYS);
  }
  return normalizeChartSeries(FIGMA_CLIENTS_CHART_VALUES, bucketCount, CLIENTS_CHART_KEYS);
};

const computeClientsChartYMax = (seriesValues) => (
  computeCountChartYMax(seriesValues, CLIENTS_CHART_Y_MAX)
);

const buildClientsYLabels = (yMax) => buildCountYLabels(yMax);

const pickVisibleSeriesValues = (visibleSeries, seriesValues) => (
  visibleSeries.reduce((result, item) => {
    const values = seriesValues?.[item.id];
    if (Array.isArray(values)) {
      result[item.id] = values;
    }
    return result;
  }, {})
);

const computeLineChartYScale = (visibleSeries, seriesValues, yAxisScale, fallbackYMax) => {
  const visibleSeriesValues = pickVisibleSeriesValues(visibleSeries, seriesValues);
  const hasVisibleValues = Object.keys(visibleSeriesValues).length > 0;
  const yMax = !hasVisibleValues
    ? fallbackYMax
    : yAxisScale === 'turnover'
      ? computeTurnoverChartYMax(visibleSeriesValues)
      : computeCountChartYMax(visibleSeriesValues, fallbackYMax);
  const resolvedYMax = yMax > 0 ? yMax : fallbackYMax;
  const yLabels = yAxisScale === 'turnover'
    ? buildTurnoverYLabels(resolvedYMax)
    : buildCountYLabels(resolvedYMax);

  return { yMax: resolvedYMax, yLabels };
};

const buildClientsSummary = (salesData) => {
  const summary = salesData?.clients?.summary;
  if (summary && typeof summary === 'object') {
    const registrations = Number(summary.registrations || 0);
    const activeEnd = Number(
      summary.active_end ?? summary.active_clients ?? summary.clients ?? 0,
    );
    const activeStart = Number(summary.active_start ?? 0);
    const conversionPercent = Number(
      summary.conversion_percent ?? summary.clients_percent ?? 0,
    );
    return {
      registrations,
      registrationsPercent: Number(summary.registrations_percent ?? (registrations > 0 ? 100 : 0)),
      clients: activeEnd,
      activeStart,
      activeEnd,
      clientsPercent: conversionPercent,
      conversionPercent,
    };
  }

  const registrations = Number(FIGMA_CLIENTS_SUMMARY.registrations);
  const clients = Number(FIGMA_CLIENTS_SUMMARY.clients);
  const conversionPercent = registrations > 0
    ? Math.round((clients / registrations) * 100)
    : FIGMA_CLIENTS_SUMMARY.conversionPercent;

  return {
    registrations,
    registrationsPercent: 100,
    clients,
    activeStart: clients,
    activeEnd: clients,
    clientsPercent: conversionPercent,
    conversionPercent,
  };
};

const buildClientsPeriodRows = (salesData) => {
  if (Array.isArray(salesData?.clients?.period_rows) && salesData.clients.period_rows.length > 0) {
    return salesData.clients.period_rows.map((row) => ({
      period: row.period,
      clients: Number(row.count ?? row.clients ?? 0),
      cr: row.cr || '',
      crTitle: row.period === 'Ушедшие' && row.cr
        ? (
          row.cr === 'нет новых'
            ? 'Новых за период 0 — доля оттока ушедшие / новые не считается'
            : 'Доля оттока к новым: ушедшие / новые. Сколько оттока относительно притока'
        )
        : '',
    }));
  }

  const scale = Number(salesData?.clients?.summary?.registrations || FIGMA_CLIENTS_SUMMARY.registrations)
    / FIGMA_CLIENTS_SUMMARY.registrations;

  return FIGMA_CLIENTS_PERIOD_ROWS.map((row) => ({
    period: row.period,
    clients: Math.round(row.clients * scale),
    cr: row.cr,
    crTitle: '',
  }));
};

const formatCrPercent = (registrations, clients) => {
  if (!registrations) {
    return '';
  }

  const percent = (clients / registrations) * 100;
  const formatted = Number.isInteger(percent)
    ? String(percent)
    : percent.toFixed(1).replace('.', ',');

  return `${formatted}%`;
};

const buildPeriodSummaryRows = (salesData) => {
  if (Array.isArray(salesData?.period_summary) && salesData.period_summary.length > 0) {
    return salesData.period_summary.map((row) => ({
      period: row.period,
      turnover: Number(row.turnover || 0),
      turnoverIndividual: Number(row.turnover_individual || 0),
      turnoverSharedPlus: Number(row.turnover_shared_plus || 0),
      turnoverShared: Number(row.turnover_shared || 0),
      turnoverMsk: Number(row.turnover_msk || 0),
      turnoverSpb: Number(row.turnover_spb || 0),
      avgCheck: Number(row.avg_check || 0),
      proxies: Number(row.proxies || 0),
      proxiesIndividual: Number(row.proxies_individual || 0),
      proxiesSharedPlus: Number(row.proxies_shared_plus || 0),
      proxiesShared: Number(row.proxies_shared || 0),
      proxiesMsk: Number(row.proxies_msk || 0),
      proxiesSpb: Number(row.proxies_spb || 0),
      activeClients: Number(row.active_clients ?? row.clients ?? 0),
      newClients: Number(row.new_clients || 0),
      registrations: Number(row.registrations || 0),
      churnedClients: Number(row.churned_clients || 0),
      cr: row.cr || '',
    }));
  }

  const turnoverScale = Number(salesData?.revenue?.total || salesData?.revenue?.week || FIGMA_GEO_SUMMARY.total)
    / FIGMA_GEO_SUMMARY.total;
  const proxyScale = Number(salesData?.proxies?.total || FIGMA_PROXY_GEO_SUMMARY.total)
    / FIGMA_PROXY_GEO_SUMMARY.total;
  const clientsScale = Number(salesData?.clients?.summary?.registrations || FIGMA_CLIENTS_SUMMARY.registrations)
    / FIGMA_CLIENTS_SUMMARY.registrations;

  return FIGMA_PERIOD_SUMMARY_ROWS.map((row) => {
    const registrations = Math.round(row.registrations * clientsScale);
    const activeClients = Math.round(row.clients * clientsScale);
    const newClients = Math.round(registrations * 0.5);
    const churnedClients = Math.round(activeClients * 0.3);

    return {
      period: row.period,
      turnover: Math.round(row.turnover * turnoverScale),
      turnoverIndividual: Math.round(row.turnover * turnoverScale * 0.35),
      turnoverSharedPlus: Math.round(row.turnover * turnoverScale * 0.4),
      turnoverShared: Math.round(row.turnover * turnoverScale * 0.25),
      turnoverMsk: Math.round(row.turnoverMsk * turnoverScale),
      turnoverSpb: Math.round(row.turnoverSpb * turnoverScale),
      avgCheck: row.avgCheck,
      proxies: Math.round(row.proxies * proxyScale),
      proxiesIndividual: Math.round(row.proxies * proxyScale * 0.35),
      proxiesSharedPlus: Math.round(row.proxies * proxyScale * 0.4),
      proxiesShared: Math.round(row.proxies * proxyScale * 0.25),
      proxiesMsk: Math.round(row.proxiesMsk * proxyScale),
      proxiesSpb: Math.round(row.proxiesSpb * proxyScale),
      activeClients,
      newClients,
      registrations,
      churnedClients,
      cr: formatCrPercent(newClients, registrations),
    };
  });
};

export const useDashboardSalesFilters = () => {
  const [geoFilter, setGeoFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('week');
  const initialRange = buildRollingRange('week', getToday());
  const [rangeStart, setRangeStart] = useState(initialRange.rangeStart);
  const [rangeEnd, setRangeEnd] = useState(initialRange.rangeEnd);

  const dateRangeLabel = useMemo(
    () => formatDateRangeLabelForRange(rangeStart, rangeEnd),
    [rangeStart, rangeEnd],
  );

  const canGoNext = useMemo(
    () => startOfDay(rangeEnd).getTime() < getToday().getTime(),
    [rangeEnd],
  );

  const applyRollingRange = useCallback((nextPeriod, endDate = getToday()) => {
    const nextRange = buildRollingRange(nextPeriod, endDate);
    setRangeStart(nextRange.rangeStart);
    setRangeEnd(nextRange.rangeEnd);
  }, []);

  const handlePrevRange = useCallback(() => {
    const spanDays = getRangeSpanDays(rangeStart, rangeEnd);
    const newEnd = addDays(rangeEnd, -spanDays);
    if (!newEnd) {
      return;
    }
    setRangeEnd(newEnd);
    setRangeStart(addDays(newEnd, -(spanDays - 1)));
  }, [rangeStart, rangeEnd]);

  const handleNextRange = useCallback(() => {
    if (!canGoNext) {
      return;
    }
    const spanDays = getRangeSpanDays(rangeStart, rangeEnd);
    const todayDate = getToday();
    let newEnd = addDays(rangeEnd, spanDays);
    if (!newEnd) {
      return;
    }
    if (newEnd.getTime() > todayDate.getTime()) {
      newEnd = todayDate;
    }
    setRangeEnd(newEnd);
    setRangeStart(addDays(newEnd, -(spanDays - 1)));
  }, [canGoNext, rangeEnd, rangeStart]);

  const handlePeriodFilterChange = useCallback((nextPeriod) => {
    setPeriodFilter(nextPeriod);
    applyRollingRange(nextPeriod, getToday());
  }, [applyRollingRange]);

  const handleCustomRangeChange = useCallback((startDate, endDate) => {
    const normalizedStart = startOfDay(startDate);
    const normalizedEnd = startOfDay(endDate);
    if (!normalizedStart || !normalizedEnd) {
      return;
    }
    const [from, to] = normalizedStart <= normalizedEnd
      ? [normalizedStart, normalizedEnd]
      : [normalizedEnd, normalizedStart];
    const spanDays = getRangeSpanDays(from, to);
    setRangeStart(from);
    setRangeEnd(to);
    setPeriodFilter(inferPeriodFromSpan(spanDays));
  }, []);

  const resetFilters = useCallback(() => {
    setGeoFilter(SALES_FILTER_DEFAULTS.geoFilter);
    setPeriodFilter(SALES_FILTER_DEFAULTS.periodFilter);
    applyRollingRange(SALES_FILTER_DEFAULTS.periodFilter, getToday());
  }, [applyRollingRange]);

  return {
    geoFilter,
    periodFilter,
    dateRangeLabel,
    rangeStart,
    rangeEnd,
    canGoNext,
    setGeoFilter,
    setPeriodFilter,
    handlePeriodFilterChange,
    handlePrevRange,
    handleNextRange,
    handleCustomRangeChange,
    resetFilters,
  };
};

const DashboardSalesFilterSheetCloseIcon = () => (
  <svg
    className="dashboard-sales-filter-sheet__close-icon"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M18 6L6 18M6 6L18 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DashboardSalesFilterSheetToggleChip = ({
  label,
  isSelected,
  onToggle,
}) => {
  const handleClick = () => {
    onToggle();
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle();
    }
  };

  return (
    <button
      type="button"
      className={`dashboard-sales-filter-sheet__chip${isSelected ? ' dashboard-sales-filter-sheet__chip--selected' : ''}`}
      aria-pressed={isSelected}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      {label}
    </button>
  );
};

const DashboardSalesFilterSheetChip = ({
  label,
  isSelected,
  value,
  onSelect,
  optionIds,
  activeId,
}) => {
  const handleClick = () => {
    onSelect(value);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect(value);
      return;
    }
    if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) {
      return;
    }
    event.preventDefault();
    const currentIndex = optionIds.findIndex((itemId) => itemId === activeId);
    if (currentIndex < 0) {
      return;
    }
    const keyToIndex = {
      ArrowRight: (currentIndex + 1) % optionIds.length,
      ArrowLeft: (currentIndex - 1 + optionIds.length) % optionIds.length,
      Home: 0,
      End: optionIds.length - 1,
    };
    onSelect(optionIds[keyToIndex[event.key]]);
  };

  return (
    <button
      type="button"
      className={`dashboard-sales-filter-sheet__chip${isSelected ? ' dashboard-sales-filter-sheet__chip--selected' : ''}`}
      aria-pressed={isSelected}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      {label}
    </button>
  );
};

export const DashboardSalesMobileFilterSheet = ({
  isOpen,
  geoFilter,
  periodFilter,
  visibleDataSections,
  onClose,
  onApply,
  onReset,
}) => {
  const [draftGeoFilter, setDraftGeoFilter] = useState(geoFilter);
  const [draftPeriodFilter, setDraftPeriodFilter] = useState(periodFilter);
  const [draftVisibleDataSections, setDraftVisibleDataSections] = useState(
    () => normalizeVisibleDataSections(visibleDataSections),
  );

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    setDraftGeoFilter(geoFilter);
    setDraftPeriodFilter(periodFilter);
    setDraftVisibleDataSections(normalizeVisibleDataSections(visibleDataSections));
    return undefined;
  }, [isOpen, geoFilter, periodFilter, visibleDataSections]);

  const handleToggleDraftDataSection = (sectionId) => {
    setDraftVisibleDataSections((currentSections) => {
      if (currentSections.includes(sectionId)) {
        return currentSections.filter((id) => id !== sectionId);
      }
      return [...currentSections, sectionId];
    });
  };

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') {
      return undefined;
    }
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  // Затемнение закрывает шторку только настоящим щелчком — нажатие и отпускание на нём (просьба владельца 28.10)
  const overlayBackdrop = useBackdropClose(onClose);

  if (!isOpen) {
    return null;
  }

  const handleApply = () => {
    onApply({
      geoFilter: draftGeoFilter,
      periodFilter: draftPeriodFilter,
      visibleDataSections: normalizeVisibleDataSections(draftVisibleDataSections),
    });
  };

  const handleReset = () => {
    onReset();
  };

  const geoFilterIds = GEO_FILTERS.map((item) => item.id);
  const periodFilterIds = MOBILE_PERIOD_FILTERS.map((item) => item.id);

  const sheetContent = (
    <div
      className="dashboard-sales-filter-sheet"
      {...overlayBackdrop}
      role="dialog"
      aria-modal="true"
      aria-labelledby="dashboard-sales-filter-sheet-title"
    >
      <div
        className="dashboard-sales-filter-sheet__panel"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="dashboard-sales-filter-sheet__header">
          <h2 className="dashboard-sales-filter-sheet__title" id="dashboard-sales-filter-sheet-title">
            Фильтр
          </h2>
          <button
            type="button"
            className="dashboard-sales-filter-sheet__close-btn"
            onClick={onClose}
            aria-label="Закрыть"
            tabIndex={0}
          >
            <DashboardSalesFilterSheetCloseIcon />
          </button>
        </div>

        <div className="dashboard-sales-filter-sheet__content">
          <section className="dashboard-sales-filter-sheet__section" aria-label="Данные">
            <h3 className="dashboard-sales-filter-sheet__section-title">Данные</h3>
            <div className="dashboard-sales-filter-sheet__chips" role="group" aria-label="Данные">
              {SALES_DATA_FILTERS.map((item) => (
                <DashboardSalesFilterSheetToggleChip
                  key={item.id}
                  label={item.label}
                  isSelected={draftVisibleDataSections.includes(item.id)}
                  onToggle={() => handleToggleDraftDataSection(item.id)}
                />
              ))}
            </div>
          </section>

          <section className="dashboard-sales-filter-sheet__section" aria-label="Город">
            <h3 className="dashboard-sales-filter-sheet__section-title">Город</h3>
            <div className="dashboard-sales-filter-sheet__chips" role="group" aria-label="Город">
              {GEO_FILTERS.map((item) => (
                <DashboardSalesFilterSheetChip
                  key={item.id}
                  label={item.label}
                  value={item.id}
                  isSelected={draftGeoFilter === item.id}
                  activeId={draftGeoFilter}
                  optionIds={geoFilterIds}
                  onSelect={setDraftGeoFilter}
                />
              ))}
            </div>
          </section>

          <section className="dashboard-sales-filter-sheet__section" aria-label="Период">
            <h3 className="dashboard-sales-filter-sheet__section-title">Период</h3>
            <div className="dashboard-sales-filter-sheet__chips" role="group" aria-label="Период">
              {MOBILE_PERIOD_FILTERS.map((item) => (
                <DashboardSalesFilterSheetChip
                  key={item.id}
                  label={item.label}
                  value={item.id}
                  isSelected={draftPeriodFilter === item.id}
                  activeId={draftPeriodFilter}
                  optionIds={periodFilterIds}
                  onSelect={setDraftPeriodFilter}
                />
              ))}
            </div>
          </section>
        </div>

        <div className="dashboard-sales-filter-sheet__footer">
          <button
            type="button"
            className="dashboard-sales-filter-sheet__action dashboard-sales-filter-sheet__action--reset"
            onClick={handleReset}
            tabIndex={0}
          >
            Сбросить
          </button>
          <button
            type="button"
            className="dashboard-sales-filter-sheet__action dashboard-sales-filter-sheet__action--apply"
            onClick={handleApply}
            tabIndex={0}
          >
            Применить
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') {
    return sheetContent;
  }

  return createPortal(sheetContent, document.body);
};

const getDashboardPickerMaxMonth = () => dayjs(getMskToday()).startOf('month');

const clampDashboardPickerMonth = (nextMonth) => {
  const maxMonth = getDashboardPickerMaxMonth();
  const normalized = dayjs(nextMonth).startOf('month');
  if (normalized.isAfter(maxMonth)) {
    return maxMonth;
  }
  return normalized;
};

const DashboardHeaderArrowIcon = ({ direction = 'left' }) => (
  <svg
    className={`dashboard-header-row__arrow-icon dashboard-header-row__arrow-icon--${direction}`}
    viewBox="0 0 12 12"
    fill="none"
    aria-hidden="true"
  >
    <path
      d={direction === 'left' ? 'M7.5 2.5L4 6L7.5 9.5' : 'M4.5 2.5L8 6L4.5 9.5'}
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const DashboardSalesDateRange = ({
  dateRangeLabel,
  rangeStart,
  rangeEnd,
  periodFilter,
  canGoNext = false,
  onPrevRange,
  onNextRange,
  onCustomRangeChange,
  onPeriodFilterChange,
  className = '',
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerValue, setPickerValue] = useState([
    dayjs(rangeStart),
    dayjs(rangeEnd),
  ]);
  const [pickerPanelMonth, setPickerPanelMonth] = useState(() => (
    clampDashboardPickerMonth(dayjs(rangeStart))
  ));
  const datePickerWrapRef = useRef(null);
  const [isMobileViewport, setIsMobileViewport] = useState(() => (
    typeof window !== 'undefined'
    && window.matchMedia(`(max-width: ${DASHBOARD_DATE_POPOVER_MOBILE_MAX_WIDTH}px)`).matches
  ));

  useEffect(() => {
    setPickerValue([dayjs(rangeStart), dayjs(rangeEnd)]);
    setPickerPanelMonth(clampDashboardPickerMonth(dayjs(rangeStart)));
  }, [rangeStart, rangeEnd]);

  useEffect(() => {
    if (!pickerOpen) {
      return;
    }
    setPickerPanelMonth(clampDashboardPickerMonth(dayjs(rangeStart)));
  }, [pickerOpen, rangeStart]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mediaQuery = window.matchMedia(`(max-width: ${DASHBOARD_DATE_POPOVER_MOBILE_MAX_WIDTH}px)`);
    const handleMediaQueryChange = (event) => {
      setIsMobileViewport(event.matches);
    };

    mediaQuery.addEventListener('change', handleMediaQueryChange);
    return () => mediaQuery.removeEventListener('change', handleMediaQueryChange);
  }, []);

  const handleApplyCustomRange = () => {
    if (!pickerValue?.[0] || !pickerValue?.[1]) {
      return;
    }
    const normalizedStart = normalizeMskCalendarDate(pickerValue[0].toDate());
    const normalizedEnd = normalizeMskCalendarDate(pickerValue[1].toDate());
    if (!normalizedStart || !normalizedEnd) {
      return;
    }
    onCustomRangeChange(normalizedStart, normalizedEnd);
    setPickerOpen(false);
  };

  const handleResetCustomRange = () => {
    const today = getMskToday();
    setPickerValue([dayjs(today), dayjs(today)]);
    setPickerPanelMonth(clampDashboardPickerMonth(dayjs(today)));
    onCustomRangeChange(today, today);
    setPickerOpen(false);
  };

  const isPickerAtMaxMonth = pickerPanelMonth.startOf('month').valueOf()
    >= getDashboardPickerMaxMonth().valueOf();

  const handleShiftPickerMonth = (delta) => {
    setPickerPanelMonth((prev) => clampDashboardPickerMonth(prev.add(delta, 'month')));
  };

  const handleShiftPickerYear = (delta) => {
    setPickerPanelMonth((prev) => clampDashboardPickerMonth(prev.add(delta, 'year')));
  };

  const handlePickerValueChange = (nextPanelValue) => {
    const raw = Array.isArray(nextPanelValue) ? nextPanelValue[0] : nextPanelValue;
    if (!raw) {
      return;
    }
    setPickerPanelMonth(clampDashboardPickerMonth(raw));
  };

  const handlePickerCalendarChange = (nextDates, _dateStrings, info) => {
    if (info?.range !== 'start' || !nextDates?.[0]) {
      return;
    }
    setPickerPanelMonth(clampDashboardPickerMonth(nextDates[0]));
  };

  const renderDatePickerPanel = (panelNode) => (
    <div className="dashboard-header-row__date-picker-panel">
      <div
        className="dashboard-header-row__date-picker-month-nav"
        role="group"
        aria-label="Переключение месяца и года"
      >
        <button
          type="button"
          className="dashboard-header-row__date-picker-month-nav-btn"
          aria-label="Предыдущий год"
          onClick={() => handleShiftPickerYear(-1)}
        >
          «
        </button>
        <button
          type="button"
          className="dashboard-header-row__date-picker-month-nav-btn"
          aria-label="Предыдущий месяц"
          onClick={() => handleShiftPickerMonth(-1)}
        >
          ‹
        </button>
        <span className="dashboard-header-row__date-picker-month-nav-label" aria-live="polite">
          {pickerPanelMonth.locale('ru').format('MMMM YYYY')}
        </span>
        <button
          type="button"
          className="dashboard-header-row__date-picker-month-nav-btn"
          aria-label="Следующий месяц"
          onClick={() => handleShiftPickerMonth(1)}
          disabled={isPickerAtMaxMonth}
        >
          ›
        </button>
        <button
          type="button"
          className="dashboard-header-row__date-picker-month-nav-btn"
          aria-label="Следующий год"
          onClick={() => handleShiftPickerYear(1)}
          disabled={isPickerAtMaxMonth}
        >
          »
        </button>
      </div>
      {panelNode}
    </div>
  );

  const datePickerContent = (
    <div className="dashboard-header-row__date-picker" ref={datePickerWrapRef}>
      <DatePicker.RangePicker
        value={pickerValue}
        onChange={(nextValue) => setPickerValue(nextValue || [null, null])}
        pickerValue={[pickerPanelMonth, pickerPanelMonth]}
        onPickerValueChange={handlePickerValueChange}
        onCalendarChange={handlePickerCalendarChange}
        panelRender={renderDatePickerPanel}
        format="DD.MM.YYYY"
        allowClear={false}
        maxDate={dayjs(getMskToday())}
        disabledDate={(current) => {
          if (!current) {
            return false;
          }
          const candidate = normalizeMskCalendarDate(current.toDate());
          if (!candidate) {
            return false;
          }
          return candidate.getTime() > getMskToday().getTime();
        }}
        className="dashboard-header-row__date-picker-input"
        placement={isMobileViewport ? 'bottomLeft' : 'bottomRight'}
        popupClassName={[
          'dashboard-header-row__date-picker-dropdown',
          'dashboard-header-row__date-picker-dropdown--single',
          isMobileViewport ? 'dashboard-header-row__date-picker-dropdown--mobile' : '',
        ].filter(Boolean).join(' ')}
        getPopupContainer={() => datePickerWrapRef.current || document.body}
      />
      <div className="dashboard-header-row__date-picker-actions">
        <button
          type="button"
          className="dashboard-header-row__date-picker-btn dashboard-header-row__date-picker-btn--reset"
          onClick={handleResetCustomRange}
        >
          Сегодня
        </button>
        <button
          type="button"
          className="dashboard-header-row__date-picker-btn dashboard-header-row__date-picker-btn--apply"
          onClick={handleApplyCustomRange}
        >
          Применить
        </button>
      </div>
    </div>
  );

  return (
    <div
      className={`dashboard-header-row__date-range dashboard-header-row__date-range--in-bar${className ? ` ${className}` : ''}`.trim()}
      aria-label="Выбранный период"
    >
      <button
        type="button"
        className="dashboard-header-row__date-nav"
        aria-label="Предыдущий период"
        onClick={onPrevRange}
      >
        <DashboardHeaderArrowIcon direction="left" />
      </button>
      <Popover
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        trigger="click"
        placement={isMobileViewport ? 'bottom' : 'bottomRight'}
        content={datePickerContent}
        overlayClassName="dashboard-header-row__date-popover"
        overlayInnerStyle={{ overflow: 'visible' }}
        getPopupContainer={() => document.body}
      >
        <button
          type="button"
          className="dashboard-header-row__date-label dashboard-header-row__date-label--button"
          aria-label="Выбрать период"
          aria-haspopup="dialog"
          aria-expanded={pickerOpen}
        >
          {dateRangeLabel}
        </button>
      </Popover>
      <button
        type="button"
        className="dashboard-header-row__date-nav"
        aria-label="Следующий период"
        onClick={onNextRange}
        disabled={!canGoNext}
      >
        <DashboardHeaderArrowIcon direction="right" />
      </button>
    </div>
  );
};

export const DashboardSalesToolbar = ({
  geoFilter,
  periodFilter,
  onGeoFilterChange,
  onPeriodFilterChange,
  onRefresh,
  refreshing = false,
  dateRangeControl = null,
}) => {
  const handleChipClick = (event, handler) => {
    const nextValue = event.currentTarget.dataset.value;
    if (!nextValue) return;
    handler(nextValue);
  };

  const handleChipKeyDown = (event, handler, options, activeValue) => {
    if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const currentIndex = options.findIndex((item) => item.id === activeValue);
    if (currentIndex < 0) return;

    const keyToIndex = {
      ArrowRight: (currentIndex + 1) % options.length,
      ArrowLeft: (currentIndex - 1 + options.length) % options.length,
      Home: 0,
      End: options.length - 1,
    };
    handler(options[keyToIndex[event.key]].id);
  };

  const getGeoChipClassName = (itemId) => {
    const isActive = geoFilter === itemId;
    const isMuted = !isActive && itemId !== 'all';
    return [
      'dashboard-header-row__chip',
      isActive ? 'dashboard-header-row__chip--active' : '',
      isMuted ? 'dashboard-header-row__chip--muted' : '',
    ].filter(Boolean).join(' ');
  };

  return (
    <div className="dashboard-header-row__actions" aria-label="Фильтры продаж">
      <div className="dashboard-header-row__filters">
        <div className="dashboard-header-row__filters-group" role="group" aria-label="Город">
          {GEO_FILTERS.map((item) => (
            <button
              key={item.id}
              type="button"
              className={getGeoChipClassName(item.id)}
              data-value={item.id}
              aria-pressed={geoFilter === item.id}
              onClick={(event) => handleChipClick(event, onGeoFilterChange)}
              onKeyDown={(event) => handleChipKeyDown(event, onGeoFilterChange, GEO_FILTERS, geoFilter)}
            >
              {item.label}
            </button>
          ))}
          <span className="dashboard-header-row__divider" aria-hidden="true" />
        </div>

        <div className="dashboard-header-row__filters-group" role="group" aria-label="Период">
          {PERIOD_FILTERS.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`dashboard-header-row__chip${periodFilter === item.id ? ' dashboard-header-row__chip--active' : ''}`}
              data-value={item.id}
              aria-pressed={periodFilter === item.id}
              onClick={(event) => handleChipClick(event, onPeriodFilterChange)}
              onKeyDown={(event) => handleChipKeyDown(event, onPeriodFilterChange, PERIOD_FILTERS, periodFilter)}
            >
              {item.label}
            </button>
          ))}
        </div>
        {dateRangeControl}

        <button
          type="button"
          className={`dashboard-header-row__refresh-btn${refreshing ? ' dashboard-header-row__refresh-btn--spinning' : ''}`}
          aria-label="Обновить данные продаж"
          onClick={onRefresh}
          disabled={refreshing}
        >
          <svg className="dashboard-header-row__refresh-icon" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path d="M6.0001 0.999894C6.66336 1.00207 7.31959 1.13576 7.93084 1.39324C8.54208 1.65071 9.09619 2.02685 9.5611 2.49989H8.0001C7.86749 2.49989 7.74032 2.55257 7.64655 2.64634C7.55278 2.74011 7.5001 2.86729 7.5001 2.99989C7.5001 3.1325 7.55278 3.25968 7.64655 3.35345C7.74032 3.44722 7.86749 3.49989 8.0001 3.49989H10.0716C10.3178 3.49976 10.5539 3.4019 10.728 3.2278C10.9021 3.0537 11 2.81761 11.0001 2.57139V0.499894C11.0001 0.367286 10.9474 0.240109 10.8537 0.14634C10.7599 0.0525723 10.6327 -0.000106158 10.5001 -0.000106158C10.3675 -0.000106158 10.2403 0.0525723 10.1465 0.14634C10.0528 0.240109 10.0001 0.367286 10.0001 0.499894V1.53889C9.17369 0.79462 8.15639 0.295099 7.06215 0.0962684C5.96791 -0.102562 4.83991 0.00714982 3.80449 0.413118C2.76907 0.819085 1.86711 1.50528 1.19961 2.39486C0.532105 3.28443 0.125422 4.34227 0.0251013 5.44989C0.0186429 5.51952 0.0267591 5.58973 0.0489327 5.65605C0.0711063 5.72237 0.106851 5.78334 0.153889 5.83509C0.200928 5.88683 0.258228 5.92821 0.32214 5.95658C0.386052 5.98496 0.455174 5.99971 0.525101 5.99989C0.647395 6.00145 0.765868 5.95731 0.857331 5.87612C0.948793 5.79492 1.00666 5.68251 1.0196 5.56089C1.13091 4.31627 1.70359 3.15825 2.62514 2.31431C3.54669 1.47038 4.75051 1.00154 6.0001 0.999894Z" fill="currentColor" />
            <path d="M11.4756 6.00004C11.3533 5.99848 11.2349 6.04262 11.1434 6.12382C11.0519 6.20501 10.9941 6.31742 10.9811 6.43904C10.8985 7.39066 10.5446 8.2986 9.96145 9.05515C9.37831 9.8117 8.5904 10.3851 7.6912 10.7073C6.79199 11.0296 5.81922 11.0871 4.8883 10.8731C3.95738 10.6591 3.10737 10.1826 2.43912 9.50005H4.00012C4.13273 9.50005 4.25991 9.44737 4.35368 9.3536C4.44744 9.25983 4.50012 9.13266 4.50012 9.00005C4.50012 8.86744 4.44744 8.74026 4.35368 8.64649C4.25991 8.55272 4.13273 8.50005 4.00012 8.50005H1.92862C1.80667 8.49998 1.6859 8.52395 1.57322 8.57059C1.46054 8.61723 1.35816 8.68562 1.27193 8.77185C1.18569 8.85808 1.1173 8.96047 1.07067 9.07315C1.02403 9.18583 1.00006 9.3066 1.00012 9.42855V11.5001C1.00012 11.6327 1.0528 11.7598 1.14657 11.8536C1.24034 11.9474 1.36751 12.0001 1.50012 12.0001C1.63273 12.0001 1.75991 11.9474 1.85368 11.8536C1.94744 11.7598 2.00012 11.6327 2.00012 11.5001V10.4611C2.82654 11.2053 3.84383 11.7048 4.93807 11.9037C6.03232 12.1025 7.16031 11.9928 8.19573 11.5868C9.23115 11.1809 10.1331 10.4947 10.8006 9.60509C11.4681 8.71551 11.8748 7.65767 11.9751 6.55004C11.9816 6.48041 11.9735 6.4102 11.9513 6.34388C11.9291 6.27756 11.8934 6.21659 11.8463 6.16485C11.7993 6.11311 11.742 6.07173 11.6781 6.04335C11.6142 6.01498 11.545 6.00023 11.4751 6.00004H11.4756Z" fill="currentColor" />
          </svg>
        </button>
      </div>
    </div>
  );
};

const DashboardSalesLineChart = ({
  title,
  ariaLabel,
  chartAriaLabel,
  xAxisLabels,
  bucketRanges = [],
  chartGranularity = null,
  granularityOptions = CHART_GRANULARITY_OPTIONS,
  onGranularityChange = null,
  yMax,
  yLabels,
  yAxisScale = 'turnover',
  series = CHART_SERIES,
  seriesValues,
  xAxisModifier = '',
  valueFormatter = defaultChartValueFormatter,
  yAxisMinLabel = '',
}) => {
  const plotOverlayRef = useRef(null);
  const [hoverState, setHoverState] = useState(null);
  const [hiddenSeriesIds, setHiddenSeriesIds] = useState(() => new Set());
  const showGranularityControl = (
    typeof onGranularityChange === 'function'
    && Array.isArray(granularityOptions)
    && granularityOptions.length > 1
  );

  const isSeriesVisible = useCallback(
    (seriesId) => !hiddenSeriesIds.has(seriesId),
    [hiddenSeriesIds],
  );

  const handleLegendToggle = useCallback((seriesId) => {
    setHiddenSeriesIds((prevHiddenSeriesIds) => {
      const nextHiddenSeriesIds = new Set(prevHiddenSeriesIds);
      if (nextHiddenSeriesIds.has(seriesId)) {
        nextHiddenSeriesIds.delete(seriesId);
      } else {
        nextHiddenSeriesIds.add(seriesId);
      }
      return nextHiddenSeriesIds;
    });
  }, []);

  const handleLegendKeyDown = useCallback((event, seriesId) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    handleLegendToggle(seriesId);
  }, [handleLegendToggle]);

  const visibleSeries = useMemo(
    () => series.filter((item) => isSeriesVisible(item.id)),
    [isSeriesVisible, series],
  );

  const { yMax: chartYMax, yLabels: chartYLabels } = useMemo(
    () => computeLineChartYScale(visibleSeries, seriesValues, yAxisScale, yMax),
    [visibleSeries, seriesValues, yAxisScale, yMax],
  );

  const bucketCount = useMemo(
    () => resolveLineChartPointCount(xAxisLabels, seriesValues, series),
    [xAxisLabels, seriesValues, series],
  );

  const chartSeries = useMemo(() => (
    visibleSeries.map((item) => ({
      ...item,
      points: buildChartPolyline(seriesValues[item.id], chartYMax),
    }))
  ), [visibleSeries, seriesValues, chartYMax]);

  const hoverMarkers = useMemo(() => {
    if (hoverState == null) {
      return [];
    }
    return visibleSeries.map((item) => {
      const value = seriesValues?.[item.id]?.[hoverState.bucketIndex] ?? 0;
      const coords = getChartPointCoords(hoverState.bucketIndex, value, chartYMax, bucketCount);
      return {
        id: item.id,
        color: item.color,
        ...coords,
      };
    });
  }, [hoverState, visibleSeries, seriesValues, chartYMax, bucketCount]);

  const handlePlotMouseMove = useCallback((event) => {
    const overlay = plotOverlayRef.current;
    if (!overlay) {
      return;
    }
    const rect = overlay.getBoundingClientRect();
    if (rect.width <= 0) {
      return;
    }
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;
    const bucketIndex = resolveChartBucketIndex(offsetX, rect.width, bucketCount);
    setHoverState({
      bucketIndex,
      offsetX,
      offsetY,
      plotWidth: rect.width,
      plotHeight: rect.height,
    });
  }, [bucketCount]);

  const handlePlotMouseLeave = useCallback(() => {
    setHoverState(null);
  }, []);

  const crosshairXPercent = hoverState
    ? resolveChartBucketXPercent(hoverState.bucketIndex, bucketCount)
    : 0;

  const tooltipStyle = useMemo(() => {
    if (!hoverState) {
      return null;
    }
    const tooltipWidth = 168;
    const tooltipHeight = 24 + visibleSeries.length * 22;
    const anchorX = (crosshairXPercent / 100) * hoverState.plotWidth;
    let left = anchorX + 12;
    if (left + tooltipWidth > hoverState.plotWidth - 4) {
      left = anchorX - tooltipWidth - 12;
    }
    left = Math.max(4, Math.min(left, hoverState.plotWidth - tooltipWidth - 4));
    let top = hoverState.offsetY - tooltipHeight / 2;
    top = Math.max(4, Math.min(top, hoverState.plotHeight - tooltipHeight - 4));
    return { left, top };
  }, [hoverState, crosshairXPercent, visibleSeries.length]);

  const bucketLabel = hoverState
    ? getChartBucketLabel(
      xAxisLabels,
      hoverState.bucketIndex,
      bucketRanges,
      chartGranularity,
    )
    : '';

  return (
    <div className={`dashboard-sales-chart-card${xAxisModifier ? ` ${xAxisModifier}` : ''}`.trim()} aria-label={ariaLabel}>
      <div className="dashboard-sales-chart-card__header">
        <h2 className="dashboard-sales-chart-card__title">{title}</h2>
        {showGranularityControl && (
          <div
            className="dashboard-sales-chart-card__granularity"
            role="group"
            aria-label="Детализация графика"
          >
            {granularityOptions.map((option) => {
              const isActive = option.id === chartGranularity;
              return (
                <button
                  key={option.id}
                  type="button"
                  className={[
                    'dashboard-sales-chart-card__granularity-btn',
                    isActive ? 'dashboard-sales-chart-card__granularity-btn--active' : '',
                  ].filter(Boolean).join(' ')}
                  aria-pressed={isActive}
                  onClick={() => onGranularityChange(option.id)}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        )}
        <ul className="dashboard-sales-chart-card__legend">
          {series.map((item) => {
            const isHidden = !isSeriesVisible(item.id);
            return (
              <li
                key={item.id}
                className={[
                  'dashboard-sales-chart-card__legend-item',
                  isHidden ? 'dashboard-sales-chart-card__legend-item--hidden' : '',
                ].filter(Boolean).join(' ')}
              >
                <button
                  type="button"
                  className="dashboard-sales-chart-card__legend-toggle"
                  aria-pressed={!isHidden}
                  aria-label={`${isHidden ? 'Показать' : 'Скрыть'} серию ${formatSeriesLegendLabel(item)}`}
                  onClick={() => handleLegendToggle(item.id)}
                  onKeyDown={(event) => handleLegendKeyDown(event, item.id)}
                  tabIndex={0}
                >
                  <span
                    className="dashboard-sales-chart-card__legend-dot"
                    style={{ backgroundColor: item.color }}
                    aria-hidden="true"
                  />
                  {formatSeriesLegendLabel(item)}
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="dashboard-sales-chart-card__plot">
        <div className="dashboard-sales-chart-card__grid" aria-hidden="true">
          {chartYLabels.map((label, index) => (
            <div key={`${label}-${index}`} className="dashboard-sales-chart-card__grid-row">
              <span className="dashboard-sales-chart-card__grid-label">{label}</span>
              <div className="dashboard-sales-chart-card__grid-track" />
            </div>
          ))}
          <div className="dashboard-sales-chart-card__grid-row dashboard-sales-chart-card__grid-row--base">
            <span className="dashboard-sales-chart-card__grid-label">{yAxisMinLabel}</span>
            <div className="dashboard-sales-chart-card__grid-track" />
          </div>
        </div>

        <svg
          className="dashboard-sales-chart-card__svg"
          viewBox={`0 0 ${CHART_SVG_WIDTH} ${CHART_SVG_HEIGHT}`}
          preserveAspectRatio="none"
          role="img"
          aria-label={chartAriaLabel}
        >
          {chartSeries.map((item) => (
            <polyline
              key={item.id}
              points={item.points}
              fill="none"
              stroke={item.color}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {hoverMarkers.map((marker) => (
            <circle
              key={marker.id}
              cx={marker.x}
              cy={marker.y}
              r="4"
              fill="#ffffff"
              stroke={marker.color}
              strokeWidth="2"
              vectorEffect="non-scaling-stroke"
            />
          ))}
        </svg>

        <div
          ref={plotOverlayRef}
          className={`dashboard-sales-chart-card__interaction${hoverState ? ' dashboard-sales-chart-card__interaction--active' : ''}`.trim()}
          onMouseMove={handlePlotMouseMove}
          onMouseLeave={handlePlotMouseLeave}
          role="presentation"
          aria-hidden="true"
        >
          {hoverState && (
            <>
              <div
                className="dashboard-sales-chart-card__crosshair dashboard-sales-chart-card__crosshair--vertical"
                style={{ left: `${crosshairXPercent}%` }}
              />
              <div
                className="dashboard-sales-chart-card__crosshair dashboard-sales-chart-card__crosshair--horizontal"
                style={{ top: `${hoverState.offsetY}px` }}
              />
              <div
                className="dashboard-sales-chart-card__tooltip"
                style={tooltipStyle}
              >
                <p className="dashboard-sales-chart-card__tooltip-title">{bucketLabel}</p>
                <ul className="dashboard-sales-chart-card__tooltip-list">
                  {visibleSeries.map((item) => {
                    const value = seriesValues?.[item.id]?.[hoverState.bucketIndex] ?? 0;
                    return (
                      <li key={item.id} className="dashboard-sales-chart-card__tooltip-row">
                        <span
                          className="dashboard-sales-chart-card__tooltip-dot"
                          style={{ backgroundColor: item.color }}
                          aria-hidden="true"
                        />
                        <span className="dashboard-sales-chart-card__tooltip-label">
                          {formatSeriesTooltipLabel(item)}
                        </span>
                        <span className="dashboard-sales-chart-card__tooltip-value">
                          {valueFormatter(value)}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="dashboard-sales-chart-card__x-axis">
        <div className="dashboard-sales-chart-card__x-axis-track">
          {xAxisLabels.map((label, index) => {
            if (!label) {
              return null;
            }
            return (
              <span
                key={`axis-${index}`}
                className="dashboard-sales-chart-card__x-label"
                style={resolveChartBucketLabelStyle(index, bucketCount)}
              >
                {label}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const DashboardSalesColumnTable = ({ columns, rows, className = '', ariaLabel }) => (
  <div className={`dashboard-sales-col-table ${className}`.trim()} role="table" aria-label={ariaLabel}>
    {columns.map((column) => (
      <div
        key={column.key}
        className={[
          'dashboard-sales-col-table__column',
          column.modifier ? `dashboard-sales-col-table__column--${column.modifier}` : '',
          column.columnClassName || '',
        ].filter(Boolean).join(' ')}
      >
        <div
          className={`dashboard-sales-col-table__cell dashboard-sales-col-table__cell--head ${column.headClassName || ''}`.trim()}
          role="columnheader"
        >
          {column.header}
        </div>
        {rows.map((row) => (
          <div
            key={row.id}
            className={`dashboard-sales-col-table__cell ${row.className || ''} ${row.cellClassNames?.[column.key] || ''}`.trim()}
            role="cell"
            title={column.key === 'cr' ? (row.title || undefined) : undefined}
          >
            {row[column.key]}
          </div>
        ))}
      </div>
    ))}
  </div>
);

const DashboardSalesGeoSummaryBlock = ({
  labelHeader = 'Всего',
  total,
  totalPercent = 100,
  rows,
  spbPercent,
  mskPercent,
  centerLabel = null,
  donutAriaLabel = null,
  ariaLabel = 'Сводка по городам',
}) => (
  <div className="dashboard-sales-aside__summary">
    <DashboardSalesColumnTable
      className="dashboard-sales-col-table--geo"
      ariaLabel={ariaLabel}
      columns={[
        { key: 'label', header: labelHeader },
        {
          key: 'value',
          header: total,
          modifier: 'value',
          headClassName: 'dashboard-sales-col-table__cell--align-right dashboard-sales-col-table__cell--geo-head-value',
        },
        {
          key: 'percent',
          header: totalPercent,
          modifier: 'percent',
          headClassName: 'dashboard-sales-col-table__cell--align-right dashboard-sales-col-table__cell--geo-head-value',
        },
      ]}
      rows={rows}
    />
    <DashboardSalesGeoDonut
      spbPercent={spbPercent}
      mskPercent={mskPercent}
      centerLabel={centerLabel}
      ariaLabel={donutAriaLabel}
    />
  </div>
);

const buildPurchaseTypeSummaryRows = ({
  newAmount,
  newPercent,
  renewalAmount,
  renewalPercent,
  withCurrency = false,
}) => [
  {
    id: 'new',
    label: 'Новые',
    value: formatMoney(newAmount, withCurrency),
    percent: formatPercent(newPercent),
    cellClassNames: {
      label: 'dashboard-sales-col-table__cell--geo-spb',
      value: 'dashboard-sales-col-table__cell--geo-spb dashboard-sales-col-table__cell--align-right',
      percent: 'dashboard-sales-col-table__cell--geo-spb dashboard-sales-col-table__cell--align-right',
    },
  },
  {
    id: 'renewal',
    label: 'Продление',
    value: formatMoney(renewalAmount, withCurrency),
    percent: formatPercent(renewalPercent),
    cellClassNames: {
      label: 'dashboard-sales-col-table__cell--geo-msk',
      value: 'dashboard-sales-col-table__cell--geo-msk dashboard-sales-col-table__cell--align-right',
      percent: 'dashboard-sales-col-table__cell--geo-msk dashboard-sales-col-table__cell--align-right',
    },
  },
];

const buildGeoSummaryRows = ({
  spbAmount,
  spbPercent,
  mskAmount,
  mskPercent,
  withCurrency = false,
}) => [
  {
    id: 'spb',
    label: 'СПб',
    value: formatMoney(spbAmount, withCurrency),
    percent: formatPercent(spbPercent),
    cellClassNames: {
      label: 'dashboard-sales-col-table__cell--geo-spb',
      value: 'dashboard-sales-col-table__cell--geo-spb dashboard-sales-col-table__cell--align-right',
      percent: 'dashboard-sales-col-table__cell--geo-spb dashboard-sales-col-table__cell--align-right',
    },
  },
  {
    id: 'msk',
    label: 'Мск',
    value: formatMoney(mskAmount, withCurrency),
    percent: formatPercent(mskPercent),
    cellClassNames: {
      label: 'dashboard-sales-col-table__cell--geo-msk',
      value: 'dashboard-sales-col-table__cell--geo-msk dashboard-sales-col-table__cell--align-right',
      percent: 'dashboard-sales-col-table__cell--geo-msk dashboard-sales-col-table__cell--align-right',
    },
  },
];

const DashboardSalesGeoDonut = ({
  spbPercent = 75,
  mskPercent = 25,
  centerLabel = null,
  ariaLabel = null,
}) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const clampedSpb = Math.min(100, Math.max(0, Math.round(Number(spbPercent) || 0)));
  const clampedMsk = Math.min(100, Math.max(0, Math.round(Number(mskPercent) || 0)));
  const resolvedAriaLabel = ariaLabel || `СПб ${clampedSpb}%, Мск ${clampedMsk}%`;

  useEffect(() => {
    if (!canvasRef.current) {
      return undefined;
    }

    const chartOptions = {
      type: 'doughnut',
      data: {
        labels: ['СПб', 'Мск'],
        datasets: [
          {
            data: [clampedSpb, clampedMsk],
            backgroundColor: ['rgba(12, 190, 144, 0.35)', 'rgba(243, 63, 78, 0.35)'],
            hoverBackgroundColor: ['rgba(12, 190, 144, 0.5)', 'rgba(243, 63, 78, 0.5)'],
            borderColor: ['#0CBE90', '#F33F4E'],
            borderWidth: 1,
            borderRadius: 2,
            spacing: 4,
          },
        ],
      },
      options: {
        cutout: '60%',
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false },
        },
        animation: {
          animateScale: true,
          animateRotate: true,
        },
      },
    };

    if (chartRef.current) {
      chartRef.current.data.datasets[0].data = [clampedSpb, clampedMsk];
      chartRef.current.update();
      return undefined;
    }

    const chart = new Chart(canvasRef.current, chartOptions);
    chartRef.current = chart;

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [clampedSpb, clampedMsk]);

  return (
    <div
      className="dashboard-sales-geo-donut"
      role="img"
      aria-label={resolvedAriaLabel}
    >
      <canvas
        className="dashboard-sales-geo-donut__canvas"
        ref={canvasRef}
        width="112"
        height="112"
        aria-hidden="true"
      />
      {centerLabel ? (
        <span className="dashboard-sales-geo-donut__center">{centerLabel}</span>
      ) : null}
    </div>
  );
};

const DashboardSalesClientsAside = ({ clientsSummary, periodRows }) => {
  const summaryRows = [
    {
      id: 'registrations',
      label: 'Регистрации',
      value: formatCount(clientsSummary.registrations),
      percent: formatPercent(clientsSummary.registrationsPercent),
      cellClassNames: {
        label: 'dashboard-sales-col-table__cell--geo-spb',
        value: 'dashboard-sales-col-table__cell--geo-spb dashboard-sales-col-table__cell--align-right',
        percent: 'dashboard-sales-col-table__cell--geo-spb dashboard-sales-col-table__cell--align-right',
      },
    },
    {
      id: 'clients',
      label: 'Активные',
      value: formatCount(clientsSummary.activeEnd ?? clientsSummary.clients),
      percent: formatPercent(clientsSummary.clientsPercent),
      cellClassNames: {
        label: 'dashboard-sales-col-table__cell--geo-msk',
        value: 'dashboard-sales-col-table__cell--geo-msk dashboard-sales-col-table__cell--align-right',
        percent: 'dashboard-sales-col-table__cell--geo-msk dashboard-sales-col-table__cell--align-right',
      },
    },
  ];

  const donutGreenPercent = Math.max(0, 100 - clientsSummary.conversionPercent);
  const donutRedPercent = clientsSummary.conversionPercent;

  const periodColumns = [
    { key: 'period', header: 'Тип', modifier: 'tariff' },
    {
      key: 'clients',
      header: 'Кол-во',
      modifier: 'numeric-xl',
      headClassName: 'dashboard-sales-col-table__cell--align-right dashboard-sales-col-table__cell--tariff-head',
    },
    {
      key: 'cr',
      header: 'CR',
      modifier: 'numeric-xl',
      headClassName: 'dashboard-sales-col-table__cell--align-right dashboard-sales-col-table__cell--tariff-total-head',
    },
  ];

  const periodTableRows = periodRows.map((row) => ({
    id: row.period,
    period: row.period,
    clients: formatCount(row.clients),
    cr: row.cr,
    title: row.crTitle || undefined,
    cellClassNames: {
      period: 'dashboard-sales-col-table__cell--tariff-data',
      clients: 'dashboard-sales-col-table__cell--tariff-data dashboard-sales-col-table__cell--align-right',
      cr: row.cr
        ? 'dashboard-sales-col-table__cell--tariff-total dashboard-sales-col-table__cell--align-right'
        : 'dashboard-sales-col-table__cell--tariff-data dashboard-sales-col-table__cell--align-right',
    },
  }));

  return (
    <aside className="dashboard-sales-turnover__aside" aria-label="Сводка клиентов">
      <div className="dashboard-sales-aside">
        <DashboardSalesGeoSummaryBlock
          labelHeader="Клиенты"
          total=""
          totalPercent=""
          rows={summaryRows}
          spbPercent={donutGreenPercent}
          mskPercent={donutRedPercent}
          centerLabel={formatPercent(clientsSummary.conversionPercent)}
          donutAriaLabel={`Конверсия ${formatPercent(clientsSummary.conversionPercent)}`}
          ariaLabel="Сводка клиентов"
        />

        <div className="dashboard-sales-tariff-card">
          <DashboardSalesColumnTable
            className="dashboard-sales-col-table--tariff"
            ariaLabel="Клиенты по срокам"
            columns={periodColumns}
            rows={periodTableRows}
          />
        </div>
      </div>
    </aside>
  );
};

const DashboardSalesPurchaseTypeAside = ({
  typeSummary,
  tariffRows,
  ariaLabel = 'Сводка новых покупок и продлений',
}) => {
  const summaryRows = buildPurchaseTypeSummaryRows({
    newAmount: typeSummary.newAmount,
    newPercent: typeSummary.newPercent,
    renewalAmount: typeSummary.renewalAmount,
    renewalPercent: typeSummary.renewalPercent,
    withCurrency: true,
  });

  const tariffColumns = [
    { key: 'tariff', header: 'Тариф', modifier: 'tariff' },
    {
      key: 'avgCheck',
      header: 'Ср.чек',
      modifier: 'numeric',
      headClassName: 'dashboard-sales-col-table__cell--align-right dashboard-sales-col-table__cell--tariff-head',
    },
    {
      key: 'new',
      header: 'Новые',
      modifier: 'purchase-type',
      headClassName: 'dashboard-sales-col-table__cell--align-right dashboard-sales-col-table__cell--tariff-head',
    },
    {
      key: 'renewal',
      header: 'Продление',
      modifier: 'purchase-type-wide',
      headClassName: 'dashboard-sales-col-table__cell--align-right dashboard-sales-col-table__cell--tariff-head',
    },
    {
      key: 'total',
      header: 'Всего',
      modifier: 'numeric',
      headClassName: 'dashboard-sales-col-table__cell--align-right dashboard-sales-col-table__cell--tariff-total-head',
    },
  ];

  const tariffTableRows = tariffRows.map((row) => ({
    id: row.tariff,
    tariff: row.tariff,
    avgCheck: formatMoney(row.avgCheck),
    new: formatMoney(row.new),
    renewal: formatMoney(row.renewal),
    total: formatMoney(row.total),
    className: 'dashboard-sales-col-table__cell--tariff-data',
    cellClassNames: {
      tariff: 'dashboard-sales-col-table__cell--tariff-data',
      avgCheck: 'dashboard-sales-col-table__cell--tariff-data dashboard-sales-col-table__cell--align-right',
      new: 'dashboard-sales-col-table__cell--tariff-data dashboard-sales-col-table__cell--align-right',
      renewal: 'dashboard-sales-col-table__cell--tariff-data dashboard-sales-col-table__cell--align-right',
      total: 'dashboard-sales-col-table__cell--tariff-total dashboard-sales-col-table__cell--align-right',
    },
  }));

  return (
    <aside className="dashboard-sales-turnover__aside" aria-label={ariaLabel}>
      <div className="dashboard-sales-aside">
        <DashboardSalesGeoSummaryBlock
          labelHeader="Всего"
          total={formatMoney(typeSummary.total)}
          totalPercent={formatPercent(typeSummary.totalPercent)}
          rows={summaryRows}
          spbPercent={typeSummary.newPercent}
          mskPercent={typeSummary.renewalPercent}
          donutAriaLabel={`Новые ${formatPercent(typeSummary.newPercent)}, Продление ${formatPercent(typeSummary.renewalPercent)}`}
          ariaLabel="Сводка по типу сделки"
        />

        <div className="dashboard-sales-tariff-card">
          <DashboardSalesColumnTable
            className="dashboard-sales-col-table--tariff dashboard-sales-col-table--purchase-type-tariff"
            ariaLabel="Тарифы по типу сделки"
            columns={tariffColumns}
            rows={tariffTableRows}
          />
        </div>
      </div>
    </aside>
  );
};

const DashboardSalesAside = ({
  geoSummary,
  tariffRows,
  showAvgCheck = true,
  geoRowWithCurrency = true,
  tariffNumericModifier = 'numeric',
  ariaLabel = 'Сводка продаж',
}) => {
  const geoRows = buildGeoSummaryRows({
    spbAmount: geoSummary.spbAmount,
    spbPercent: geoSummary.spbPercent,
    mskAmount: geoSummary.mskAmount,
    mskPercent: geoSummary.mskPercent,
    withCurrency: geoRowWithCurrency,
  });

  const tariffColumns = [
    { key: 'tariff', header: 'Тариф', modifier: 'tariff' },
    ...(showAvgCheck ? [{
      key: 'avgCheck',
      header: 'Ср.чек',
      modifier: tariffNumericModifier,
      headClassName: 'dashboard-sales-col-table__cell--align-right dashboard-sales-col-table__cell--tariff-head',
    }] : []),
    {
      key: 'msk',
      header: 'МСК',
      modifier: tariffNumericModifier,
      headClassName: 'dashboard-sales-col-table__cell--align-right dashboard-sales-col-table__cell--tariff-head',
    },
    {
      key: 'spb',
      header: 'СПб',
      modifier: tariffNumericModifier,
      headClassName: 'dashboard-sales-col-table__cell--align-right dashboard-sales-col-table__cell--tariff-head',
    },
    {
      key: 'total',
      header: 'Всего',
      modifier: tariffNumericModifier,
      headClassName: 'dashboard-sales-col-table__cell--align-right dashboard-sales-col-table__cell--tariff-total-head',
    },
  ];

  const tariffTableRows = tariffRows.map((row) => ({
    id: row.tariff,
    tariff: row.tariff,
    avgCheck: row.avgCheck != null ? formatMoney(row.avgCheck) : undefined,
    msk: formatMoney(row.msk),
    spb: formatMoney(row.spb),
    total: formatMoney(row.total),
    className: 'dashboard-sales-col-table__cell--tariff-data',
    cellClassNames: {
      tariff: 'dashboard-sales-col-table__cell--tariff-data',
      avgCheck: 'dashboard-sales-col-table__cell--tariff-data dashboard-sales-col-table__cell--align-right',
      msk: 'dashboard-sales-col-table__cell--tariff-data dashboard-sales-col-table__cell--align-right',
      spb: 'dashboard-sales-col-table__cell--tariff-data dashboard-sales-col-table__cell--align-right',
      total: 'dashboard-sales-col-table__cell--tariff-total dashboard-sales-col-table__cell--align-right',
    },
  }));

  return (
    <aside className="dashboard-sales-turnover__aside" aria-label={ariaLabel}>
      <div className="dashboard-sales-aside">
        <DashboardSalesGeoSummaryBlock
          labelHeader="Всего"
          total={formatMoney(geoSummary.total)}
          totalPercent={formatPercent(geoSummary.totalPercent)}
          rows={geoRows}
          spbPercent={geoSummary.spbPercent}
          mskPercent={geoSummary.mskPercent}
          ariaLabel="Сводка по городам"
        />

        <div className="dashboard-sales-tariff-card">
          <DashboardSalesColumnTable
            className="dashboard-sales-col-table--tariff"
            ariaLabel="Тарифы"
            columns={tariffColumns}
            rows={tariffTableRows}
          />
        </div>
      </div>
    </aside>
  );
};

const PERIOD_SUMMARY_PERIOD_CELL = 'dashboard-sales-col-table__cell--period-data-muted';
const PERIOD_SUMMARY_MUTED_CELL = 'dashboard-sales-col-table__cell--period-data-muted dashboard-sales-col-table__cell--align-right';
const PERIOD_SUMMARY_STRONG_CELL = 'dashboard-sales-col-table__cell--period-data-strong dashboard-sales-col-table__cell--align-right';

const PERIOD_SUMMARY_COLUMNS = [
  { key: 'period', header: 'Срок', modifier: 'period-label' },
  {
    key: 'turnover',
    header: 'Оборот',
    modifier: 'period-numeric',
    headClassName: 'dashboard-sales-col-table__cell--align-right dashboard-sales-col-table__cell--period-head-strong',
  },
  {
    key: 'turnoverIndividual',
    header: 'Инд.',
    modifier: 'period-numeric',
    headClassName: 'dashboard-sales-col-table__cell--align-right dashboard-sales-col-table__cell--period-head-muted',
  },
  {
    key: 'turnoverSharedPlus',
    header: 'Общий+',
    modifier: 'period-numeric',
    headClassName: 'dashboard-sales-col-table__cell--align-right dashboard-sales-col-table__cell--period-head-muted',
  },
  {
    key: 'turnoverShared',
    header: 'Общий',
    modifier: 'period-numeric',
    headClassName: 'dashboard-sales-col-table__cell--align-right dashboard-sales-col-table__cell--period-head-muted',
  },
  {
    key: 'turnoverMsk',
    header: 'МСК',
    modifier: 'period-numeric',
    headClassName: 'dashboard-sales-col-table__cell--align-right dashboard-sales-col-table__cell--period-head-muted',
  },
  {
    key: 'turnoverSpb',
    header: 'СПб',
    modifier: 'period-numeric',
    headClassName: 'dashboard-sales-col-table__cell--align-right dashboard-sales-col-table__cell--period-head-muted',
  },
  {
    key: 'avgCheck',
    header: 'Средний чек',
    modifier: 'period-numeric',
    columnClassName: 'dashboard-sales-col-table__column--period-divider-right',
    headClassName: 'dashboard-sales-col-table__cell--align-right dashboard-sales-col-table__cell--period-head-muted',
  },
  {
    key: 'proxies',
    header: 'Созданные прокси',
    modifier: 'period-numeric',
    columnClassName: 'dashboard-sales-col-table__column--period-divider-left',
    headClassName: 'dashboard-sales-col-table__cell--align-right dashboard-sales-col-table__cell--period-head-strong',
  },
  {
    key: 'proxiesIndividual',
    header: 'Инд.',
    modifier: 'period-numeric',
    headClassName: 'dashboard-sales-col-table__cell--align-right dashboard-sales-col-table__cell--period-head-muted',
  },
  {
    key: 'proxiesSharedPlus',
    header: 'Общий+',
    modifier: 'period-numeric',
    headClassName: 'dashboard-sales-col-table__cell--align-right dashboard-sales-col-table__cell--period-head-muted',
  },
  {
    key: 'proxiesShared',
    header: 'Общий',
    modifier: 'period-numeric',
    headClassName: 'dashboard-sales-col-table__cell--align-right dashboard-sales-col-table__cell--period-head-muted',
  },
  {
    key: 'proxiesMsk',
    header: 'МСК',
    modifier: 'period-numeric',
    headClassName: 'dashboard-sales-col-table__cell--align-right dashboard-sales-col-table__cell--period-head-muted',
  },
  {
    key: 'proxiesSpb',
    header: 'СПб',
    modifier: 'period-numeric',
    columnClassName: 'dashboard-sales-col-table__column--period-divider-right',
    headClassName: 'dashboard-sales-col-table__cell--align-right dashboard-sales-col-table__cell--period-head-muted',
  },
  {
    key: 'activeClients',
    header: 'Активные (конец)',
    modifier: 'period-numeric',
    columnClassName: 'dashboard-sales-col-table__column--period-divider-left',
    headClassName: 'dashboard-sales-col-table__cell--align-right dashboard-sales-col-table__cell--period-head-strong',
  },
  {
    key: 'newClients',
    header: 'Новые',
    modifier: 'period-numeric',
    headClassName: 'dashboard-sales-col-table__cell--align-right dashboard-sales-col-table__cell--period-head-muted',
  },
  {
    key: 'registrations',
    header: 'Регистрации',
    modifier: 'period-numeric',
    headClassName: 'dashboard-sales-col-table__cell--align-right dashboard-sales-col-table__cell--period-head-emphasis',
  },
  {
    key: 'churnedClients',
    header: 'Ушедшие',
    modifier: 'period-numeric',
    headClassName: 'dashboard-sales-col-table__cell--align-right dashboard-sales-col-table__cell--period-head-muted',
  },
  {
    key: 'cr',
    header: 'CR',
    modifier: 'period-numeric',
    headClassName: 'dashboard-sales-col-table__cell--align-right dashboard-sales-col-table__cell--period-head-muted',
  },
];

const DashboardSalesPeriodSummary = ({ rows }) => {
  const tableRows = rows.map((row) => ({
    id: row.period,
    period: row.period,
    turnover: formatMoney(row.turnover),
    turnoverIndividual: formatMoney(row.turnoverIndividual),
    turnoverSharedPlus: formatMoney(row.turnoverSharedPlus),
    turnoverShared: formatMoney(row.turnoverShared),
    turnoverMsk: formatMoney(row.turnoverMsk),
    turnoverSpb: formatMoney(row.turnoverSpb),
    avgCheck: formatMoney(row.avgCheck),
    proxies: formatCount(row.proxies),
    proxiesIndividual: formatCount(row.proxiesIndividual),
    proxiesSharedPlus: formatCount(row.proxiesSharedPlus),
    proxiesShared: formatCount(row.proxiesShared),
    proxiesMsk: formatCount(row.proxiesMsk),
    proxiesSpb: formatCount(row.proxiesSpb),
    activeClients: formatCount(row.activeClients),
    newClients: formatCount(row.newClients),
    registrations: formatCount(row.registrations),
    churnedClients: formatCount(row.churnedClients),
    cr: row.cr,
    cellClassNames: {
      period: PERIOD_SUMMARY_PERIOD_CELL,
      turnover: PERIOD_SUMMARY_STRONG_CELL,
      turnoverIndividual: PERIOD_SUMMARY_MUTED_CELL,
      turnoverSharedPlus: PERIOD_SUMMARY_MUTED_CELL,
      turnoverShared: PERIOD_SUMMARY_MUTED_CELL,
      turnoverMsk: PERIOD_SUMMARY_MUTED_CELL,
      turnoverSpb: PERIOD_SUMMARY_MUTED_CELL,
      avgCheck: PERIOD_SUMMARY_MUTED_CELL,
      proxies: PERIOD_SUMMARY_STRONG_CELL,
      proxiesIndividual: PERIOD_SUMMARY_MUTED_CELL,
      proxiesSharedPlus: PERIOD_SUMMARY_MUTED_CELL,
      proxiesShared: PERIOD_SUMMARY_MUTED_CELL,
      proxiesMsk: PERIOD_SUMMARY_MUTED_CELL,
      proxiesSpb: PERIOD_SUMMARY_MUTED_CELL,
      activeClients: PERIOD_SUMMARY_STRONG_CELL,
      newClients: PERIOD_SUMMARY_MUTED_CELL,
      registrations: PERIOD_SUMMARY_STRONG_CELL,
      churnedClients: PERIOD_SUMMARY_MUTED_CELL,
      cr: PERIOD_SUMMARY_MUTED_CELL,
    },
  }));

  return (
    <article className="dashboard-sales-period-summary" aria-label="Сводка по периодам">
      <DashboardSalesColumnTable
        className="dashboard-sales-col-table--period"
        ariaLabel="Сводка по периодам"
        columns={PERIOD_SUMMARY_COLUMNS}
        rows={tableRows}
      />
    </article>
  );
};

/**
 * Панель «Продажи» — макет Figma 26-04 wa Proxy Manager (node 884:45857).
 */
const buildChartViewModel = (
  salesData,
  periodFilter,
  rangeStart,
  rangeEnd,
  chartGranularity,
) => {
  const granularity = getChartGranularity(
    salesData,
    periodFilter,
    rangeStart,
    rangeEnd,
    chartGranularity,
  );
  const bucketCount = getChartBucketCount(
    periodFilter,
    salesData,
    rangeStart,
    rangeEnd,
    granularity,
  );
  const bucketRanges = getChartBucketRanges(salesData);
  const xAxisLabels = buildXAxisLabels(
    rangeStart,
    rangeEnd,
    bucketCount,
    granularity,
    bucketRanges,
  );
  const spanDays = getRangeSpanDays(rangeStart, rangeEnd);
  let xAxisModifier = '';
  if (granularity === 'hour' || spanDays <= 1) {
    xAxisModifier = 'dashboard-sales-chart-card--x-hourly';
  } else if (granularity === 'day' && spanDays > 7) {
    xAxisModifier = 'dashboard-sales-chart-card--x-dense';
  }
  return {
    granularity,
    bucketCount,
    bucketRanges,
    xAxisLabels,
    xAxisModifier,
  };
};

const DashboardSalesPanel = ({
  stats = null,
  salesData = null,
  salesDataByGranularity = null,
  autoChartGranularity = null,
  onEnsureChartGranularity = null,
  geoFilter = 'all',
  periodFilter = 'week',
  rangeStart = null,
  rangeEnd = null,
  visibleDataSections = SALES_DATA_SECTIONS_DEFAULT,
}) => {
  const resolvedAutoGranularity = useMemo(
    () => autoChartGranularity || resolveAutoChartGranularity(periodFilter, rangeStart, rangeEnd),
    [autoChartGranularity, periodFilter, rangeStart, rangeEnd],
  );
  const [chartGranularityById, setChartGranularityById] = useState({});

  useEffect(() => {
    setChartGranularityById({});
  }, [rangeStart, rangeEnd, periodFilter, geoFilter]);

  const granularityOptions = useMemo(() => {
    if (resolvedAutoGranularity === 'hour') {
      return [{ id: 'hour', label: 'Часы' }];
    }
    return CHART_GRANULARITY_OPTIONS;
  }, [resolvedAutoGranularity]);

  const resolveChartGranularity = useCallback((chartId) => (
    chartGranularityById[chartId] || resolvedAutoGranularity
  ), [chartGranularityById, resolvedAutoGranularity]);

  const resolveChartSalesData = useCallback((chartId) => {
    const granularity = resolveChartGranularity(chartId);
    if (salesDataByGranularity && salesDataByGranularity[granularity]) {
      return salesDataByGranularity[granularity];
    }
    const metaGranularity = String(salesData?.meta?.chart_granularity || '').trim().toLowerCase();
    if (metaGranularity === granularity) {
      return salesData;
    }
    // Ждём fetch нужной детализации — не подмешиваем чужие bucket'ы / Figma-моки.
    return {
      meta: {
        chart_granularity: granularity,
        chart_buckets: getChartBucketCount(
          periodFilter,
          null,
          rangeStart,
          rangeEnd,
          granularity,
        ),
        chart_bucket_ranges: [],
      },
      chart: {},
      client_purchases: { chart: {} },
      proxies: { chart: {} },
      new_proxies: { chart: {} },
      clients: { chart: {} },
    };
  }, [
    resolveChartGranularity,
    salesDataByGranularity,
    salesData,
    periodFilter,
    rangeStart,
    rangeEnd,
  ]);

  const handleChartGranularityChange = useCallback((chartId, granularity) => {
    setChartGranularityById((prev) => (
      prev[chartId] === granularity ? prev : { ...prev, [chartId]: granularity }
    ));
    if (typeof onEnsureChartGranularity === 'function') {
      void onEnsureChartGranularity(granularity);
    }
  }, [onEnsureChartGranularity]);

  const turnoverGeoSummary = useMemo(() => buildGeoSummary(salesData), [salesData]);
  const turnoverTariffRows = useMemo(() => buildTariffRows(salesData, geoFilter), [salesData, geoFilter]);
  const clientPurchaseTypeSummary = useMemo(
    () => buildClientPurchaseTypeSummary(salesData),
    [salesData],
  );
  const clientPurchaseTariffRows = useMemo(
    () => buildClientPurchaseTariffRows(salesData),
    [salesData],
  );

  const turnoverChartData = resolveChartSalesData(CHART_IDS.turnover);
  const turnoverGranularity = resolveChartGranularity(CHART_IDS.turnover);
  const turnoverChartView = useMemo(
    () => buildChartViewModel(
      turnoverChartData,
      periodFilter,
      rangeStart,
      rangeEnd,
      turnoverGranularity,
    ),
    [turnoverChartData, periodFilter, rangeStart, rangeEnd, turnoverGranularity],
  );
  const turnoverChartSeries = useMemo(
    () => buildSalesChartSeries(
      turnoverChartData,
      rangeStart,
      rangeEnd,
      periodFilter,
      turnoverGranularity,
    ),
    [turnoverChartData, rangeStart, rangeEnd, periodFilter, turnoverGranularity],
  );
  const turnoverChartYMax = useMemo(
    () => computeTurnoverChartYMax(turnoverChartSeries),
    [turnoverChartSeries],
  );
  const turnoverChartYLabels = useMemo(
    () => buildTurnoverYLabels(turnoverChartYMax),
    [turnoverChartYMax],
  );

  const clientPurchaseChartData = resolveChartSalesData(CHART_IDS.clientPurchases);
  const clientPurchaseGranularity = resolveChartGranularity(CHART_IDS.clientPurchases);
  const clientPurchaseChartView = useMemo(
    () => buildChartViewModel(
      clientPurchaseChartData,
      periodFilter,
      rangeStart,
      rangeEnd,
      clientPurchaseGranularity,
    ),
    [clientPurchaseChartData, periodFilter, rangeStart, rangeEnd, clientPurchaseGranularity],
  );
  const clientPurchaseChartSeries = useMemo(
    () => buildClientPurchaseChartSeries(
      clientPurchaseChartData,
      rangeStart,
      rangeEnd,
      periodFilter,
      clientPurchaseGranularity,
    ),
    [clientPurchaseChartData, rangeStart, rangeEnd, periodFilter, clientPurchaseGranularity],
  );
  const clientPurchaseChartYMax = useMemo(
    () => computeTurnoverChartYMax(clientPurchaseChartSeries),
    [clientPurchaseChartSeries],
  );
  const clientPurchaseChartYLabels = useMemo(
    () => buildTurnoverYLabels(clientPurchaseChartYMax),
    [clientPurchaseChartYMax],
  );

  const proxyGeoSummary = useMemo(() => buildProxyGeoSummary(salesData, geoFilter), [salesData, geoFilter]);
  const proxyTariffRows = useMemo(() => buildProxyTariffRows(salesData, geoFilter), [salesData, geoFilter]);
  const proxyChartData = resolveChartSalesData(CHART_IDS.proxies);
  const proxyGranularity = resolveChartGranularity(CHART_IDS.proxies);
  const proxyChartView = useMemo(
    () => buildChartViewModel(
      proxyChartData,
      periodFilter,
      rangeStart,
      rangeEnd,
      proxyGranularity,
    ),
    [proxyChartData, periodFilter, rangeStart, rangeEnd, proxyGranularity],
  );
  const proxyChartSeries = useMemo(
    () => buildProxyChartSeries(
      proxyChartData,
      rangeStart,
      rangeEnd,
      periodFilter,
      'proxies',
      proxyGranularity,
    ),
    [proxyChartData, rangeStart, rangeEnd, periodFilter, proxyGranularity],
  );
  const proxyChartYMax = useMemo(
    () => computeProxyChartYMax(proxyChartSeries),
    [proxyChartSeries],
  );
  const proxyChartYLabels = useMemo(
    () => buildProxyYLabels(proxyChartYMax),
    [proxyChartYMax],
  );

  const newProxyGeoSummary = useMemo(
    () => buildProxyGeoSummary(salesData, geoFilter, 'new_proxies'),
    [salesData, geoFilter],
  );
  const newProxyTariffRows = useMemo(
    () => buildProxyTariffRows(salesData, geoFilter, 'new_proxies'),
    [salesData, geoFilter],
  );
  const newProxyChartData = resolveChartSalesData(CHART_IDS.newProxies);
  const newProxyGranularity = resolveChartGranularity(CHART_IDS.newProxies);
  const newProxyChartView = useMemo(
    () => buildChartViewModel(
      newProxyChartData,
      periodFilter,
      rangeStart,
      rangeEnd,
      newProxyGranularity,
    ),
    [newProxyChartData, periodFilter, rangeStart, rangeEnd, newProxyGranularity],
  );
  const newProxyChartSeries = useMemo(
    () => buildProxyChartSeries(
      newProxyChartData,
      rangeStart,
      rangeEnd,
      periodFilter,
      'new_proxies',
      newProxyGranularity,
    ),
    [newProxyChartData, rangeStart, rangeEnd, periodFilter, newProxyGranularity],
  );
  const newProxyChartYMax = useMemo(
    () => computeProxyChartYMax(newProxyChartSeries),
    [newProxyChartSeries],
  );
  const newProxyChartYLabels = useMemo(
    () => buildProxyYLabels(newProxyChartYMax),
    [newProxyChartYMax],
  );

  const clientsChartData = resolveChartSalesData(CHART_IDS.clients);
  const clientsGranularity = resolveChartGranularity(CHART_IDS.clients);
  const clientsChartView = useMemo(
    () => buildChartViewModel(
      clientsChartData,
      periodFilter,
      rangeStart,
      rangeEnd,
      clientsGranularity,
    ),
    [clientsChartData, periodFilter, rangeStart, rangeEnd, clientsGranularity],
  );
  const clientsChartSeries = useMemo(
    () => buildClientsChartSeries(
      clientsChartData,
      rangeStart,
      rangeEnd,
      periodFilter,
      clientsGranularity,
    ),
    [clientsChartData, rangeStart, rangeEnd, periodFilter, clientsGranularity],
  );
  const clientsChartYMax = useMemo(
    () => computeClientsChartYMax(clientsChartSeries),
    [clientsChartSeries],
  );
  const clientsChartYLabels = useMemo(
    () => buildClientsYLabels(clientsChartYMax),
    [clientsChartYMax],
  );
  const clientsSummary = useMemo(() => buildClientsSummary(salesData), [salesData]);
  const clientsPeriodRows = useMemo(() => buildClientsPeriodRows(salesData), [salesData]);
  const periodSummaryRows = useMemo(() => buildPeriodSummaryRows(salesData), [salesData]);

  return (
    <section className="dashboard-sales-panel" aria-label="Продажи">
      <article
        className="dashboard-sales-turnover"
        hidden={isSalesDataSectionHidden(visibleDataSections, 'turnover')}
      >
        <div className="dashboard-sales-turnover__body">
          <div className="dashboard-sales-turnover__chart-wrap">
            <DashboardSalesLineChart
              title="Оборот"
              ariaLabel="Оборот"
              chartAriaLabel="График оборота по тарифам"
              xAxisLabels={turnoverChartView.xAxisLabels}
              bucketRanges={turnoverChartView.bucketRanges}
              chartGranularity={turnoverChartView.granularity}
              granularityOptions={granularityOptions}
              onGranularityChange={(granularity) => (
                handleChartGranularityChange(CHART_IDS.turnover, granularity)
              )}
              yMax={turnoverChartYMax}
              yLabels={turnoverChartYLabels}
              seriesValues={turnoverChartSeries}
              xAxisModifier={turnoverChartView.xAxisModifier}
              valueFormatter={(value) => formatMoney(value, true)}
            />
          </div>

          <DashboardSalesAside
            geoSummary={turnoverGeoSummary}
            tariffRows={turnoverTariffRows}
            showAvgCheck
            geoRowWithCurrency
            ariaLabel="Сводка оборота"
          />
        </div>
      </article>

      <article
        className="dashboard-sales-client-purchases"
        hidden={isSalesDataSectionHidden(visibleDataSections, 'turnover')}
      >
        <div className="dashboard-sales-client-purchases__body">
          <div className="dashboard-sales-client-purchases__chart-wrap">
            <DashboardSalesLineChart
              title="Продажи"
              ariaLabel="Продажи"
              chartAriaLabel="График продаж: всего, новые покупки и продления"
              xAxisLabels={clientPurchaseChartView.xAxisLabels}
              bucketRanges={clientPurchaseChartView.bucketRanges}
              chartGranularity={clientPurchaseChartView.granularity}
              granularityOptions={granularityOptions}
              onGranularityChange={(granularity) => (
                handleChartGranularityChange(CHART_IDS.clientPurchases, granularity)
              )}
              yMax={clientPurchaseChartYMax}
              yLabels={clientPurchaseChartYLabels}
              series={CLIENT_PURCHASE_CHART_SERIES}
              seriesValues={clientPurchaseChartSeries}
              xAxisModifier={clientPurchaseChartView.xAxisModifier}
              valueFormatter={(value) => formatMoney(value, true)}
            />
          </div>

          <DashboardSalesPurchaseTypeAside
            typeSummary={clientPurchaseTypeSummary}
            tariffRows={clientPurchaseTariffRows}
            ariaLabel="Сводка новых покупок и продлений"
          />
        </div>
      </article>

      <article
        className="dashboard-sales-proxies"
        hidden={isSalesDataSectionHidden(visibleDataSections, 'proxies')}
      >
        <div className="dashboard-sales-proxies__body">
          <div className="dashboard-sales-proxies__chart-wrap">
            <DashboardSalesLineChart
              title="прокси"
              ariaLabel="Прокси"
              chartAriaLabel="График активных прокси по тарифам"
              xAxisLabels={proxyChartView.xAxisLabels}
              bucketRanges={proxyChartView.bucketRanges}
              chartGranularity={proxyChartView.granularity}
              granularityOptions={granularityOptions}
              onGranularityChange={(granularity) => (
                handleChartGranularityChange(CHART_IDS.proxies, granularity)
              )}
              yMax={proxyChartYMax}
              yLabels={proxyChartYLabels}
              yAxisScale="count"
              seriesValues={proxyChartSeries}
              xAxisModifier={proxyChartView.xAxisModifier}
              yAxisMinLabel="0"
            />
          </div>

          <DashboardSalesAside
            geoSummary={proxyGeoSummary}
            tariffRows={proxyTariffRows}
            showAvgCheck={false}
            geoRowWithCurrency={false}
            tariffNumericModifier="numeric-wide"
            ariaLabel="Сводка прокси"
          />
        </div>
      </article>

      <article
        className="dashboard-sales-new-proxies"
        hidden={isSalesDataSectionHidden(visibleDataSections, 'new_proxies')}
      >
        <div className="dashboard-sales-new-proxies__body">
          <div className="dashboard-sales-new-proxies__chart-wrap">
            <DashboardSalesLineChart
              title="Новые прокси"
              ariaLabel="Новые прокси"
              chartAriaLabel="График новых прокси по тарифам"
              xAxisLabels={newProxyChartView.xAxisLabels}
              bucketRanges={newProxyChartView.bucketRanges}
              chartGranularity={newProxyChartView.granularity}
              granularityOptions={granularityOptions}
              onGranularityChange={(granularity) => (
                handleChartGranularityChange(CHART_IDS.newProxies, granularity)
              )}
              yMax={newProxyChartYMax}
              yLabels={newProxyChartYLabels}
              yAxisScale="count"
              seriesValues={newProxyChartSeries}
              xAxisModifier={newProxyChartView.xAxisModifier}
              yAxisMinLabel="0"
            />
          </div>

          <DashboardSalesAside
            geoSummary={newProxyGeoSummary}
            tariffRows={newProxyTariffRows}
            showAvgCheck={false}
            geoRowWithCurrency={false}
            tariffNumericModifier="numeric-wide"
            ariaLabel="Сводка новых прокси"
          />
        </div>
      </article>

      <article
        className="dashboard-sales-clients"
        hidden={isSalesDataSectionHidden(visibleDataSections, 'users')}
      >
        <div className="dashboard-sales-clients__body">
          <div className="dashboard-sales-clients__chart-wrap">
            <DashboardSalesLineChart
              title="Клиенты"
              ariaLabel="Клиенты"
              chartAriaLabel="График клиентов"
              xAxisLabels={clientsChartView.xAxisLabels}
              bucketRanges={clientsChartView.bucketRanges}
              chartGranularity={clientsChartView.granularity}
              granularityOptions={granularityOptions}
              onGranularityChange={(granularity) => (
                handleChartGranularityChange(CHART_IDS.clients, granularity)
              )}
              yMax={clientsChartYMax}
              yLabels={clientsChartYLabels}
              yAxisScale="count"
              series={CLIENTS_CHART_SERIES}
              seriesValues={clientsChartSeries}
              xAxisModifier={clientsChartView.xAxisModifier}
              yAxisMinLabel="0"
            />
          </div>

          <DashboardSalesClientsAside
            clientsSummary={clientsSummary}
            periodRows={clientsPeriodRows}
          />
        </div>
      </article>

      <DashboardSalesPeriodSummary rows={periodSummaryRows} />
    </section>
  );
};

export default DashboardSalesPanel;
